import {
  AppBar,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  SvgIcon,
  useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import ContrastRoundedIcon from "@mui/icons-material/ContrastRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import ReportRoundedIcon from "@mui/icons-material/ReportRounded";
import ThumbUpRoundedIcon from "@mui/icons-material/ThumbUpRounded";
import ThumbDownRoundedIcon from "@mui/icons-material/ThumbDownRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { api } from "./api";
import type { PostDetail, PostListItem } from "./types";
import { MAX_CODE_BYTES, ScoreRampOverrides, byteLenUtf8, copyToClipboard, downloadTextFile, mapApiError, repLabel, scoreColorRamp } from "./utils";

type TabKey = "feed" | "favorites" | "publish" | "profile" | "download" | "help";
type VersionMode = "per_edition" | "global";
type PublishEdition = {
  id: string;
  title: string;
  description: string;
  code: string;
  files: Array<{ name: string; size: number; text: string }>;
};

const MAX_EDITIONS = 20;

function normalizeEditionKey(input: string) {
  const base = String(input || "")
    .toLowerCase()
    .replace(/[^a-z0-9а-яё_-]+/gi, "_")
    .replace(/^_+|_+$/g, "");
  return base || "default";
}

function getInitialTabFromHash(): TabKey {
  if (typeof location === "undefined") return "feed";
  const h = (location.hash || "").replace(/^#/, "");
  if (!h.startsWith("tab=")) return "feed";
  const raw = decodeURIComponent(h.slice(4));
  const allowed: TabKey[] = ["feed", "favorites", "publish", "profile", "download", "help"];
  return (allowed.includes(raw as TabKey) ? raw : "feed") as TabKey;
}

function DiscordLogoIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M20.3 4.37A19.8 19.8 0 0 0 15.58 3a13.5 13.5 0 0 0-.6 1.23 18.4 18.4 0 0 0-5.96 0A13.5 13.5 0 0 0 8.42 3a19.7 19.7 0 0 0-4.73 1.37C.7 8.9-.12 13.32.29 17.67a19.9 19.9 0 0 0 5.8 2.94c.47-.63.9-1.3 1.28-2a12.8 12.8 0 0 1-2-.97c.17-.13.33-.27.49-.41a14 14 0 0 0 12.29 0c.16.14.32.28.49.41-.63.38-1.31.71-2.01.97.38.7.81 1.37 1.29 2a19.9 19.9 0 0 0 5.81-2.94c.48-5.04-.82-9.42-3.7-13.3ZM8.57 14.98c-1.2 0-2.18-1.1-2.18-2.46s.96-2.46 2.18-2.46c1.23 0 2.21 1.1 2.19 2.46 0 1.36-.97 2.46-2.19 2.46Zm6.86 0c-1.2 0-2.19-1.1-2.19-2.46s.97-2.46 2.19-2.46 2.2 1.1 2.18 2.46c0 1.36-.96 2.46-2.18 2.46Z"
      />
    </SvgIcon>
  );
}

function buildTheme(mode: "light" | "dark") {
  return createTheme({
    palette: {
      mode,
      ...(mode === "light"
        ? {
            primary: { main: "#2563eb" },
            background: { default: "#f6f7fb", paper: "#ffffff" },
          }
        : {
            primary: { main: "#a8c7ff" },
            background: { default: "#0f141a", paper: "#141b24" },
          }),
    },
    shape: { borderRadius: 14 },
    typography: { fontFamily: "Inter, system-ui, Segoe UI, Arial" },
  });
}

export default function App() {
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("theme");
    if (saved === "light" || saved === "dark") return saved;
    return prefersDark ? "dark" : "light";
  });
  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Root
        mode={mode}
        onToggleMode={() => {
          const next = mode === "dark" ? "light" : "dark";
          setMode(next);
          localStorage.setItem("theme", next);
        }}
      />
    </ThemeProvider>
  );
}

function Root({ mode, onToggleMode }: { mode: "light" | "dark"; onToggleMode: () => void }) {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [me, setMe] = useState<{ id: string; username: string; avatar?: string; isAdmin: boolean; nameColor?: string; topbarColor?: string } | null>(null);
  const [tab, setTab] = useState<TabKey>(() => getInitialTabFromHash());
  const [posts, setPosts] = useState<PostListItem[]>([]);
  const [favorites, setFavorites] = useState<PostListItem[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [post, setPost] = useState<PostDetail | null>(null);
  const [postActiveConfigId, setPostActiveConfigId] = useState<string>("");
  const [postOpen, setPostOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeFiles, setCodeFiles] = useState<Array<{ name: string; size: number; text: string }>>([]);
  const [codeActiveIdx, setCodeActiveIdx] = useState<number>(-1);
  const [userOpen, setUserOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<{ id: string; username: string; avatar?: string; nameColor?: string; topbarColor?: string } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchItems, setSearchItems] = useState<Array<{ id: string; username: string; avatar?: string; nameColor?: string; totalScore?: number }>>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [userStats, setUserStats] = useState<{
    postsCount: number;
    totalScore: number;
    avgScore: number;
  } | null>(null);
  const [userPosts, setUserPosts] = useState<PostListItem[]>([]);
  const [userSort, setUserSort] = useState<"latest" | "top">("latest");
  const [userCursor, setUserCursor] = useState<string | null>(null);
  const [userHasMore, setUserHasMore] = useState(false);
  const [userPaging, setUserPaging] = useState(false);
  const [feedSort, setFeedSort] = useState<"latest" | "top">("latest");
  const [feedTierFilters, setFeedTierFilters] = useState<Array<"player" | "gamer" | "skilled" | "expert" | "hero" | "king" | "legend">>([]);
  const [feedTagFilter, setFeedTagFilter] = useState("");
  const [feedMaxActions, setFeedMaxActions] = useState<number | "">("");
  const [feedQueryInput, setFeedQueryInput] = useState("");
  const [feedQuery, setFeedQuery] = useState("");
  const [feedCursor, setFeedCursor] = useState<string | null>(null);
  const [feedHasMore, setFeedHasMore] = useState(false);
  const [feedPaging, setFeedPaging] = useState(false);
  const [inlineEdit, setInlineEdit] = useState(false);
  const [inlineTitle, setInlineTitle] = useState("");
  const [inlineDesc, setInlineDesc] = useState("");
  const [inlineCode, setInlineCode] = useState("");
  const [inlineFiles, setInlineFiles] = useState<Array<{ name: string; size: number; text: string }>>([]);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [postVersionsOpen, setPostVersionsOpen] = useState(false);
  const [postVersionsLoading, setPostVersionsLoading] = useState(false);
  const [postVersions, setPostVersions] = useState<Array<{ id: string; postId: string; version: number; title: string; description: string; code: string; createdAt: number }>>([]);
  const [postVersionsShowAll, setPostVersionsShowAll] = useState(false);
  const [selectedVersionCode, setSelectedVersionCode] = useState<string>("");
  const [rollbackBusy, setRollbackBusy] = useState<number | null>(null);
  const [deletePostBusy, setDeletePostBusy] = useState(false);

  const [pubTitle, setPubTitle] = useState("");
  const [pubDesc, setPubDesc] = useState("");
  const [pubVersionMode, setPubVersionMode] = useState<VersionMode>("per_edition");
  const [pubInitialVersionMode, setPubInitialVersionMode] = useState<VersionMode>("per_edition");
  const [pubEditions, setPubEditions] = useState<PublishEdition[]>([
    { id: "default", title: "Базовое", description: "", code: "", files: [] },
  ]);
  const [pubActiveEditionId, setPubActiveEditionId] = useState("default");
  const [pubDropActive, setPubDropActive] = useState(false);
  const [pubStatus, setPubStatus] = useState<string>("");
  const [publishBusy, setPublishBusy] = useState(false);
  const [draftStatus, setDraftStatus] = useState<string>("");
  const [editPostId, setEditPostId] = useState<string | null>(null);
  const [editReturnPostId, setEditReturnPostId] = useState<string | null>(null);
  const [pubModeConfirmOpen, setPubModeConfirmOpen] = useState(false);
  const [pubPendingMode, setPubPendingMode] = useState<VersionMode | null>(null);
  const [descPreview, setDescPreview] = useState(false);
  const [profileUsername, setProfileUsername] = useState("");
  const [profileAvatar, setProfileAvatar] = useState("");
  const [profileNameColor, setProfileNameColor] = useState<string>("#ffffff");
  const [profileTopbarColor, setProfileTopbarColor] = useState<string>("#111827");
  const [profileStats, setProfileStats] = useState<{
    postsCount: number;
    totalScore: number;
    avgScore: number;
  } | null>(null);
  const [profilePosts, setProfilePosts] = useState<PostListItem[]>([]);
  const [profileCursor, setProfileCursor] = useState<string | null>(null);
  const [profileHasMore, setProfileHasMore] = useState(false);
  const [profilePaging, setProfilePaging] = useState(false);
  const bootstrappedRef = useRef(false);
  const postDetailCacheRef = useRef(new Map<string, { ts: number; data: PostDetail }>());
  const postDetailInFlightRef = useRef(new Set<string>());
  const openPostRequestSeqRef = useRef(0);
  const prefetchedNextFeedPageRef = useRef<{
    key: string;
    items: PostListItem[];
    nextCursor: string | null;
  } | null>(null);

  const [err, setErr] = useState<string>("");
  const [rampOverrides, setRampOverrides] = useState<ScoreRampOverrides>({});
  const [adminZeroColor, setAdminZeroColor] = useState<string>("#99adff");
  const [adminZeroAccent, setAdminZeroAccent] = useState<string>("#b3c0ff");
  const [adminPostCardLightFrom, setAdminPostCardLightFrom] = useState<string>("#ffffff");
  const [adminPostCardLightTo, setAdminPostCardLightTo] = useState<string>("#eff6ff");
  const [discordUrl, setDiscordUrl] = useState<string>("");
  const [adminUsersOpen, setAdminUsersOpen] = useState(false);
  const [adminUsersQ, setAdminUsersQ] = useState("");
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersItems, setAdminUsersItems] = useState<
    Array<{
      id: string;
      username: string;
      avatar?: string;
      nameColor?: string;
      email?: string;
      discordId?: string;
      googleId?: string;
      totalScore?: number;
      banned?: number | boolean;
      bannedReason?: string | null;
      bannedAt?: number | null;
    }>
  >([]);
  const [adminBanOpen, setAdminBanOpen] = useState(false);
  const [adminBanUser, setAdminBanUser] = useState<(typeof adminUsersItems)[number] | null>(null);
  const [adminBanReason, setAdminBanReason] = useState("");
  const [adminDeleteOpen, setAdminDeleteOpen] = useState(false);
  const [adminDeleteUser, setAdminDeleteUser] = useState<(typeof adminUsersItems)[number] | null>(null);
  const [adminBackupBusy, setAdminBackupBusy] = useState(false);
  const [adminBackupResult, setAdminBackupResult] = useState("");
  const [adminRecalcBusy, setAdminRecalcBusy] = useState(false);
  const [adminRecalcResult, setAdminRecalcResult] = useState("");
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentBody, setEditingCommentBody] = useState<string>("");
  const [voteLockUntil, setVoteLockUntil] = useState<Record<string, number>>({});
  // Upload-token UX removed (too complex for newcomers). Keep only "Connect MLDSL" flow.

  function flashErr(message: string, ms = 5000) {
    setErr(message);
    window.setTimeout(() => {
      setErr((prev) => (prev === message ? "" : prev));
    }, ms);
  }

  async function downloadPostZip() {
    if (!post) return;
    const files = codeFiles.length ? codeFiles : parsePostFiles(post.code || "");
    if (!files.length) {
      flashErr("Нет файлов для скачивания");
      return;
    }
    try {
      const mod = await import("jszip");
      const JSZipCtor = (mod as any).default || (mod as any);
      const zip = new JSZipCtor();
      for (const f of files) {
        const name = (f.name || "").trim() || "file.mldsl";
        zip.file(name, f.text || "");
      }
      const blob: Blob = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `post-${post.id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setPubStatus("скачано");
      setTimeout(() => setPubStatus(""), 900);
    } catch (e: any) {
      flashErr(`Не удалось собрать zip: ${String(e?.message || e)}`);
    }
  }

  const pubActiveEdition = useMemo(
    () =>
      pubEditions.find((e) => String(e.id) === String(pubActiveEditionId)) ||
      pubEditions[0] || { id: "default", title: "Базовое", description: "", code: "", files: [] as Array<{ name: string; size: number; text: string }> },
    [pubEditions, pubActiveEditionId]
  );
  const codeBytes = useMemo(() => byteLenUtf8(pubActiveEdition.code || ""), [pubActiveEdition.code]);
  const descBytes = useMemo(() => byteLenUtf8(pubDesc), [pubDesc]);
  const publishHasPlan = useMemo(
    () => (pubActiveEdition.files || []).some((f) => (f.name || "").toLowerCase() === "plan.json"),
    [pubActiveEdition.files]
  );
  const publishHasSources = useMemo(
    () => (pubActiveEdition.files || []).some((f) => (f.name || "").toLowerCase().endsWith(".mldsl")),
    [pubActiveEdition.files]
  );
  const draftKey = "mldsl_publish_draft_v1";

  async function openPostVersions() {
    if (!post) return;
    setPostVersionsOpen(true);
    setPostVersionsLoading(true);
    setSelectedVersionCode("");
    try {
      const cfg = String(postActiveConfigId || (post as any)?.activeConfigId || "");
      const r = await api.postVersions(post.id, { configId: cfg || undefined, all: postVersionsShowAll });
      if ("error" in r) throw new Error(mapApiError(r.error));
      setPostVersions(r.items || []);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setPostVersionsLoading(false);
    }
  }

  function buildQuickDiff(oldCode: string, newCode: string, maxLines = 120) {
    const a = String(oldCode || "").split(/\r?\n/);
    const b = String(newCode || "").split(/\r?\n/);
    const n = Math.max(a.length, b.length);
    const out: string[] = [];
    for (let i = 0; i < n; i += 1) {
      const left = a[i] ?? "";
      const right = b[i] ?? "";
      if (left === right) continue;
      if (left) out.push(`- ${left}`);
      if (right) out.push(`+ ${right}`);
      if (out.length >= maxLines) break;
    }
    return out.length ? out.join("\n") : "Изменений не найдено.";
  }

  function buildBundleFromFiles(files: Array<{ name: string; size: number; text: string }>) {
    return files
      .map((f) => `# --- file: ${f.name} (${f.size} B) ---\n\n${(f.text || "").trim()}\n`)
      .join("\n");
  }

  function upsertFilesByName(
    base: Array<{ name: string; size: number; text: string }>,
    incoming: Array<{ name: string; size: number; text: string }>
  ) {
    const next = [...base];
    for (const inc of incoming) {
      const idx = next.findIndex((x) => String(x.name || "").toLowerCase() === String(inc.name || "").toLowerCase());
      if (idx >= 0) next[idx] = inc;
      else next.push(inc);
    }
    return next;
  }

  function updateActiveEdition(patch: Partial<PublishEdition>) {
    const targetId = String(pubActiveEditionId || "");
    setPubEditions((prev) =>
      prev.map((e) => (String(e.id) === targetId ? { ...e, ...patch } : e))
    );
  }

  function createEdition(seedTitle = "Новое издание") {
    if (pubEditions.length >= MAX_EDITIONS) {
      flashErr(`Лимит изданий: ${MAX_EDITIONS}`);
      return;
    }
    const base = normalizeEditionKey(seedTitle);
    const existing = new Set(pubEditions.map((x) => String(x.id)));
    let idx = 1;
    let id = base;
    while (existing.has(id)) {
      idx += 1;
      id = `${base}_${idx}`;
    }
    const next: PublishEdition = { id, title: seedTitle, description: "", code: "", files: [] };
    setPubEditions((prev) => [...prev, next]);
    setPubActiveEditionId(id);
  }

  function removeEdition(id: string) {
    if (pubEditions.length <= 1) {
      flashErr("Нельзя удалить последнее издание");
      return;
    }
    setPubEditions((prev) => {
      const next = prev.filter((x) => String(x.id) !== String(id));
      if (!next.length) return prev;
      return next;
    });
    if (String(pubActiveEditionId) === String(id)) {
      const fallback = pubEditions.find((x) => String(x.id) !== String(id));
      setPubActiveEditionId(String(fallback?.id || "default"));
    }
  }

  function moveEdition(id: string, dir: -1 | 1) {
    setPubEditions((prev) => {
      const idx = prev.findIndex((x) => String(x.id) === String(id));
      if (idx < 0) return prev;
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.splice(nextIdx, 0, item);
      return next;
    });
  }

  async function appendPublishFiles(rawFiles: File[]) {
    const files = Array.from(rawFiles || []);
    if (!files.length) return;
    const bad = files.find((x) => {
      const n = x.name.toLowerCase();
      return !(n.endsWith(".mldsl") || n.endsWith(".json"));
    });
    if (bad) {
      flashErr("Все файлы должны иметь расширение .mldsl или .json");
      return;
    }

    const items: Array<{ name: string; size: number; text: string }> = [];
    for (const f of files) {
      items.push({ name: f.name, size: f.size, text: await f.text() });
    }
    const next = upsertFilesByName(pubActiveEdition.files || [], items);
    setErr("");
    updateActiveEdition({ files: next, code: buildBundleFromFiles(next) });
  }

  async function appendInlineFiles(rawFiles: File[]) {
    const files = Array.from(rawFiles || []);
    if (!files.length) return;
    const bad = files.find((x) => {
      const n = x.name.toLowerCase();
      return !(n.endsWith(".mldsl") || n.endsWith(".json"));
    });
    if (bad) {
      flashErr("Все файлы должны иметь расширение .mldsl или .json");
      return;
    }
    const items: Array<{ name: string; size: number; text: string }> = [];
    for (const f of files) {
      items.push({ name: f.name, size: f.size, text: await f.text() });
    }
    const next = upsertFilesByName(inlineFiles, items);
    setInlineFiles(next);
    setInlineCode(buildBundleFromFiles(next));
  }

  async function refreshMe() {
    const r = await api.me();
    if ("error" in r) {
      const msg = mapApiError(r.error);
      setErr(r.hint ? `${msg}\n${r.hint}` : msg);
      return null;
    }
    const nextMe = r.user
      ? {
          id: r.user.id,
          username: r.user.username,
          avatar: r.user.avatar,
          isAdmin: !!r.isAdmin,
          nameColor: r.user.nameColor,
          topbarColor: r.user.topbarColor,
        }
      : null;
    setMe(nextMe);
    return nextMe;
  }

  async function refreshFavorites() {
    if (!me) return;
    const r = await api.favorites();
    if ("error" in r) throw new Error(mapApiError(r.error));
    setFavorites(r.items || []);
    setFavoriteIds(new Set((r.items || []).map((x) => x.id)));
  }

  const POST_DETAIL_CACHE_TTL_MS = 90_000;

  function normalizeFeedQueryKey(q: string) {
    return String(q || "").trim().toLowerCase();
  }

  function feedPageKey(sort: "latest" | "top", q: string, cursor: string | null) {
    return `${sort}|${normalizeFeedQueryKey(q)}|${String(cursor || "")}`;
  }

  function readCachedPostDetail(id: string): PostDetail | null {
    const key = String(id || "");
    if (!key) return null;
    const hit = postDetailCacheRef.current.get(key);
    if (!hit) return null;
    if (Date.now() - Number(hit.ts || 0) > POST_DETAIL_CACHE_TTL_MS) {
      postDetailCacheRef.current.delete(key);
      return null;
    }
    return hit.data || null;
  }

  function writeCachedPostDetails(items: PostDetail[]) {
    const now = Date.now();
    for (const it of items || []) {
      const id = String((it as any)?.id || "");
      if (!id) continue;
      postDetailCacheRef.current.set(id, { ts: now, data: it });
    }
  }

  function deriveCachedPostForConfig(base: PostDetail, configId?: string): PostDetail {
    if (!configId) return base;
    const cfg = Array.isArray((base as any)?.configs)
      ? (base as any).configs.find((x: any) => String(x?.id || "") === String(configId))
      : null;
    if (!cfg || typeof cfg.code !== "string") return base;
    const out: PostDetail = {
      ...base,
      activeConfigId: String(cfg.id || configId),
      code: String(cfg.code || base.code || ""),
      effectiveCode: String(cfg.code || (base as any)?.effectiveCode || base.code || ""),
      requiredTier: String(cfg.requiredTier || base.requiredTier || "player"),
      requiredTierLevel: Number(cfg.requiredTierLevel ?? base.requiredTierLevel ?? 0),
    };
    return out;
  }

  function closePostModal() {
    openPostRequestSeqRef.current += 1;
    setPostOpen(false);
  }

  async function prefetchPostBatch(ids: string[]) {
    const uniq = Array.from(new Set((ids || []).map((x) => String(x || "").trim()).filter(Boolean))).slice(0, 30);
    if (!uniq.length) return;
    const toFetch = uniq.filter((id) => !readCachedPostDetail(id) && !postDetailInFlightRef.current.has(id));
    if (!toFetch.length) return;
    for (const id of toFetch) postDetailInFlightRef.current.add(id);
    try {
      const r = await api.postBatch(toFetch);
      if ("error" in r) return;
      writeCachedPostDetails(r.items || []);
    } finally {
      for (const id of toFetch) postDetailInFlightRef.current.delete(id);
    }
  }

  async function prefetchNextFeedPage(cursor: string | null, sort: "latest" | "top", q: string) {
    if (!cursor) {
      prefetchedNextFeedPageRef.current = null;
      return;
    }
    const key = feedPageKey(sort, q, cursor);
    if (prefetchedNextFeedPageRef.current?.key === key) return;
    const r = await api.listPaged({ sort, cursor, limit: 30, q });
    if ("error" in r) return;
    const items = r.items || [];
    prefetchedNextFeedPageRef.current = {
      key,
      items,
      nextCursor: r.nextCursor || null,
    };
    await prefetchPostBatch(items.map((x) => x.id));
  }

  async function refreshFeed() {
    setLoading(true);
    try {
      prefetchedNextFeedPageRef.current = null;
      const r = await api.listPaged({ sort: feedSort, limit: 30, q: feedQuery });
      if ("error" in r) throw new Error(mapApiError(r.error));
      const page = r.items || [];
      setPosts(page);
      setFeedCursor(r.nextCursor || null);
      setFeedHasMore(!!r.nextCursor);
      prefetchPostBatch(page.map((x) => x.id)).catch(() => null);
      prefetchNextFeedPage(r.nextCursor || null, feedSort, feedQuery).catch(() => null);
    } finally {
      setLoading(false);
    }
  }

  async function loadMoreFeed() {
    if (feedPaging || !feedHasMore) return;
    setFeedPaging(true);
    try {
      const cur = feedCursor || null;
      const key = feedPageKey(feedSort, feedQuery, cur);
      let page: PostListItem[] = [];
      let nextCursor: string | null = null;
      const pref = prefetchedNextFeedPageRef.current;
      if (pref && pref.key === key) {
        page = pref.items || [];
        nextCursor = pref.nextCursor || null;
        prefetchedNextFeedPageRef.current = null;
      } else {
        const r = await api.listPaged({ sort: feedSort, cursor: cur, limit: 30, q: feedQuery });
        if ("error" in r) throw new Error(mapApiError(r.error));
        page = r.items || [];
        nextCursor = r.nextCursor || null;
      }
      setPosts((prev) => [...prev, ...page]);
      setFeedCursor(nextCursor);
      setFeedHasMore(!!nextCursor);
      prefetchPostBatch(page.map((x) => x.id)).catch(() => null);
      prefetchNextFeedPage(nextCursor, feedSort, feedQuery).catch(() => null);
    } finally {
      setFeedPaging(false);
    }
  }

  async function openPost(id: string, actor: { id: string; isAdmin: boolean } | null = null, configId?: string) {
    const requestSeq = ++openPostRequestSeqRef.current;
    const hash = configId
      ? `post=${encodeURIComponent(id)}&config=${encodeURIComponent(configId)}`
      : `post=${encodeURIComponent(id)}`;
    location.hash = hash;
    const cached = readCachedPostDetail(id);
    const cachedResolved = cached ? deriveCachedPostForConfig(cached, configId) : null;
    const canUseCached =
      !!cachedResolved &&
      (!configId || String((cachedResolved as any)?.activeConfigId || "") === String(configId));
    if (canUseCached && cachedResolved && requestSeq === openPostRequestSeqRef.current) {
      setPost(cachedResolved);
      setPostActiveConfigId(String((cachedResolved as any)?.activeConfigId || configId || ""));
      setPostOpen(true);
      setCodeOpen(false);
      setCodeActiveIdx(-1);
    }
    const r = await api.get(id, configId);
    if (requestSeq !== openPostRequestSeqRef.current) return;
    if ("error" in r) {
      if (canUseCached) {
        setErr(mapApiError(r.error));
        return;
      }
      throw new Error(mapApiError(r.error));
    }
    writeCachedPostDetails([r]);
    const currentActor = actor ?? (me ? { id: me.id, isAdmin: !!me.isAdmin } : null);
    console.log("[post-debug] openPost identity", {
      postId: id,
      meId: currentActor?.id ?? null,
      meIdType: typeof currentActor?.id,
      postAuthorId: (r as any)?.authorId ?? null,
      postAuthorIdType: typeof (r as any)?.authorId,
      meIsAdmin: !!currentActor?.isAdmin,
    });
    console.log("[post-debug] openPost payload", {
      postId: id,
      requestedConfigId: String(configId || ""),
      resolvedActiveConfigId: String((r as any)?.activeConfigId || ""),
      effectiveCodeLen: Number(String((r as any)?.effectiveCode || (r as any)?.code || "").length || 0),
      requiredTier: String((r as any)?.requiredTier || ""),
      requiredTierLevel: Number((r as any)?.requiredTierLevel ?? -1),
      minRequiredTier: String((r as any)?.minRequiredTier || ""),
      maxRequiredTier: String((r as any)?.maxRequiredTier || ""),
      configsCount: Number(((r as any)?.configs || []).length || 0),
    });
    setPost(r);
    setPostActiveConfigId(String((r as any)?.activeConfigId || configId || ""));
    const cfgCount = Number((r as any)?.configs?.length || 0);
    const reqLevelRaw = Number((r as any)?.requiredTierLevel);
    const reqLevel = Number.isFinite(reqLevelRaw) ? reqLevelRaw : -1;
    let minLevel = Number((r as any)?.minRequiredTierLevel);
    let maxLevel = Number((r as any)?.maxRequiredTierLevel);
    if (!Number.isFinite(minLevel)) minLevel = reqLevel;
    if (!Number.isFinite(maxLevel)) maxLevel = reqLevel >= 0 ? reqLevel : minLevel;
    if (reqLevel >= 0 && maxLevel < reqLevel) maxLevel = reqLevel;
    // For single-edition posts do not downshift list rank by stale aggregate fields.
    if (cfgCount <= 1 && reqLevel >= 0) {
      minLevel = reqLevel;
      maxLevel = reqLevel;
    }
    const safeMinTier = tierFromLevel(minLevel);
    const safeMaxTier = tierFromLevel(maxLevel);
    const syncRank = (x: PostListItem): PostListItem =>
      x.id === id
        ? {
            ...x,
            requiredTier: safeMinTier || x.requiredTier,
            requiredTierLevel: Number.isFinite(minLevel) ? minLevel : x.requiredTierLevel,
            minRequiredTier: safeMinTier || x.minRequiredTier || x.requiredTier,
            minRequiredTierLevel: Number.isFinite(minLevel) ? minLevel : x.minRequiredTierLevel,
            maxRequiredTier: safeMaxTier || x.maxRequiredTier || x.requiredTier,
            maxRequiredTierLevel: Number.isFinite(maxLevel) ? maxLevel : x.maxRequiredTierLevel,
            configsCount: cfgCount || x.configsCount,
          }
        : x;
    setPosts((prev) => prev.map(syncRank));
    setFavorites((prev) => prev.map(syncRank));
    setProfilePosts((prev) => prev.map(syncRank));
    setUserPosts((prev) => prev.map(syncRank));
    setPostOpen(true);
    setCodeOpen(false);
    setCodeActiveIdx(-1);
  }

  async function openUser(id: string) {
    location.hash = `user=${encodeURIComponent(id)}`;
    setUserOpen(true);
    setUserId(id);
    setUserInfo(null);
    setUserStats(null);
    setUserPosts([]);
    setUserCursor(null);
    setUserHasMore(false);
    setUserSort("latest");

    const p = await api.userProfile(id);
    if ("error" in p) throw new Error(mapApiError(p.error));
    setUserInfo({ id: p.user.id, username: p.user.username, avatar: p.user.avatar, nameColor: p.user.nameColor, topbarColor: p.user.topbarColor });
    setUserStats(p.stats);

    const r = await api.userPostsPaged(id, { sort: "latest", limit: 30 });
    if ("error" in r) throw new Error(mapApiError(r.error));
    setUserPosts(r.items || []);
    setUserCursor(r.nextCursor || null);
    setUserHasMore(!!r.nextCursor);
  }

  async function loadMoreUser() {
    if (!userId || userPaging || !userHasMore) return;
    setUserPaging(true);
    try {
      const r = await api.userPostsPaged(userId, { sort: userSort, cursor: userCursor, limit: 30 });
      if ("error" in r) throw new Error(mapApiError(r.error));
      setUserPosts((prev) => [...prev, ...(r.items || [])]);
      setUserCursor(r.nextCursor || null);
      setUserHasMore(!!r.nextCursor);
    } finally {
      setUserPaging(false);
    }
  }

  function applyHashRoute(currentMe: { id: string; isAdmin: boolean } | null) {
    const h = (location.hash || "").replace(/^#/, "");
    if (!h) return;
    if (h.startsWith("post=")) {
      const rest = h.slice(5);
      const [idRaw, ...parts] = rest.split("&");
      const id = decodeURIComponent(idRaw || "");
      const configPart = parts.find((x) => x.startsWith("config="));
      const config = configPart ? decodeURIComponent(configPart.slice(7)) : undefined;
      openPost(id, currentMe, config).catch((e) => setErr(String(e?.message || e)));
      return;
    }
    if (h.startsWith("user=")) {
      const id = decodeURIComponent(h.slice(5));
      openUser(id).catch((e) => setErr(String(e?.message || e)));
      return;
    }
    if (h.startsWith("tab=")) {
      const raw = decodeURIComponent(h.slice(4));
      const allowed: TabKey[] = ["feed", "favorites", "publish", "profile", "download", "help"];
      const next = (allowed.includes(raw as TabKey) ? raw : "feed") as TabKey;
      if ((next === "favorites" || next === "profile") && !currentMe) {
        setTab("feed");
      } else {
        setTab(next);
      }
    }
  }

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    (async () => {
      const currentMe = await refreshMe().catch(() => null);
      refreshFeed().catch((e) => setErr(String(e?.message || e)));
      api
        .settings()
        .then((r) => {
          if ("error" in r) return;
          setRampOverrides({ zeroBase: r.scoreZeroColor, zeroAccent: r.scoreZeroAccent });
          setAdminZeroColor(r.scoreZeroColor);
          setAdminZeroAccent(r.scoreZeroAccent);
          setAdminPostCardLightFrom(r.postCardLightFrom || "#ffffff");
          setAdminPostCardLightTo(r.postCardLightTo || "#eff6ff");
          setDiscordUrl(String((r as any).discordUrl || ""));
        })
        .catch(() => null);
      applyHashRoute(currentMe ? { id: currentMe.id, isAdmin: !!currentMe.isAdmin } : null);
    })();
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const currentMe = me ? { id: me.id, isAdmin: !!me.isAdmin } : null;
      applyHashRoute(currentMe);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [me?.id, me?.isAdmin]);

  useEffect(() => {
    if (postOpen || userOpen) return;
    if (tab === "feed") {
      if (location.hash.startsWith("#tab=") || location.hash.startsWith("#post=") || location.hash.startsWith("#user=")) {
        history.replaceState(null, "", location.pathname + location.search);
      }
      return;
    }
    const nextHash = `#tab=${encodeURIComponent(tab)}`;
    if (location.hash !== nextHash) history.replaceState(null, "", `${location.pathname}${location.search}${nextHash}`);
  }, [tab, postOpen, userOpen]);

  useEffect(() => {
    // When sort changes, reset and reload.
    refreshFeed().catch((e) => setErr(String(e?.message || e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedSort, feedQuery]);

  useEffect(() => {
    if (!me) {
      setFavorites([]);
      setFavoriteIds(new Set());
      setProfileStats(null);
      setProfileUsername("");
      setProfileAvatar("");
      setProfileNameColor("#ffffff");
      setProfileTopbarColor("#111827");
      setProfilePosts([]);
      setProfileCursor(null);
      setProfileHasMore(false);
      return;
    }
    refreshFavorites().catch((e) => setErr(String(e?.message || e)));
    api
      .profile()
      .then((r) => {
        if ("error" in r) throw new Error(mapApiError(r.error));
        setProfileUsername(r.user.username || "");
        setProfileAvatar(r.user.avatar || "");
        setProfileNameColor((r.user.nameColor as string) || me.nameColor || "#ffffff");
        setProfileTopbarColor((r.user.topbarColor as string) || me.topbarColor || "#111827");
        setProfileStats(r.stats);
      })
      .catch((e) => setErr(String(e?.message || e)));

    api
      .myPostsPaged({ sort: "latest", limit: 30 })
      .then((r) => {
        if ("error" in r) throw new Error(mapApiError(r.error));
        setProfilePosts(r.items || []);
        setProfileCursor(r.nextCursor || null);
        setProfileHasMore(!!r.nextCursor);
      })
      .catch((e) => setErr(String(e?.message || e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.username, me?.avatar, me?.isAdmin]);

  // tokenStatus polling removed

  async function loadMoreProfilePosts() {
    if (profilePaging || !profileHasMore) return;
    setProfilePaging(true);
    try {
      const r = await api.myPostsPaged({ sort: "latest", cursor: profileCursor, limit: 30 });
      if ("error" in r) throw new Error(mapApiError(r.error));
      setProfilePosts((prev) => [...prev, ...(r.items || [])]);
      setProfileCursor(r.nextCursor || null);
      setProfileHasMore(!!r.nextCursor);
    } finally {
      setProfilePaging(false);
    }
  }

  const loginUrlDiscord = "/api/auth/discord/login";
  const loginUrlGoogle = "/api/auth/google/login";
  const [loginOpen, setLoginOpen] = useState(false);

  const rep = post ? repLabel(post.score) : null;
  const isFav = !!(post && favoriteIds.has(post.id));
  const myScore = profileStats?.totalScore ?? 0;
  const myRamp = scoreColorRamp(myScore, rampOverrides);
  const myScoreSx = {
    background: `linear-gradient(135deg, ${myRamp.base}, ${myRamp.accent})`,
    color: myScore <= 0 ? "rgba(15,20,26,0.92)" : "rgba(255,255,255,0.92)",
    border: "1px solid rgba(255,255,255,0.16)",
  } as const;

  function scoreChipSx(score: number) {
    const ramp = scoreColorRamp(score, rampOverrides);
    return {
      background: `linear-gradient(135deg, ${ramp.base}, ${ramp.accent})`,
      color: score <= 0 ? "rgba(15,20,26,0.92)" : "rgba(255,255,255,0.92)",
      border: "1px solid rgba(255,255,255,0.14)",
    } as const;
  }

  // Tier colors are aligned with Minecraft formatting codes:
  // gamer=&e, skilled=&a, expert=&b, hero=&c, king=&2, legend=&9.
  function hsvHex(h: number, s: number, v: number): string {
    const hh = ((h % 360) + 360) % 360;
    const c = v * s;
    const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
    const m = v - c;
    let r = 0;
    let g = 0;
    let b = 0;
    if (hh < 60) [r, g, b] = [c, x, 0];
    else if (hh < 120) [r, g, b] = [x, c, 0];
    else if (hh < 180) [r, g, b] = [0, c, x];
    else if (hh < 240) [r, g, b] = [0, x, c];
    else if (hh < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];
    const toHex = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  function makeTier(label: string, hue: number) {
    const h = ((hue % 360) + 360) % 360;
    const s = 1;
    const v = 1;
    const solidBg = hsvHex(h, s, v);
    const bg = "transparent";
    const border = hsvHex(h, Math.min(1, s * 0.95), Math.max(0.2, v * 0.72));
    const borderLight = hsvHex(h, Math.max(0.2, s * 0.55), Math.min(1, Math.max(v, 0.9)));
    const fg = border;
    return {
      label,
      main: solidBg,
      bg,
      fg,
      border,
      bgDark: bg,
      fgDark: fg,
      borderDark: borderLight,
    };
  }

  const tierPalette: Record<
    string,
    {
      label: string;
      main: string;
      bg: string;
      fg: string;
      border: string;
      bgDark?: string;
      fgDark?: string;
      borderDark?: string;
    }
  > = {
    player: makeTier("Игрок", 210),
    gamer: makeTier("Gamer", 40),
    skilled: makeTier("Skilled", 120),
    expert: makeTier("Expert", 185),
    hero: makeTier("Hero", 0),
    king: makeTier("King", 135),
    legend: makeTier("Legend", 255),
  };

  function tierLabel(tier?: string) {
    const raw = String(tier || "player").toLowerCase();
    const key = raw === "support" ? "player" : raw;
    return tierPalette[key]?.label || "Игрок";
  }

  function tierPrintLabel(tier?: string) {
    return `Ранг для печати: ${tierLabel(tier)}`;
  }

  function tierListLabel(tier?: string) {
    return `Печать от ${tierLabel(tier)}`;
  }

  function tierRangeLabel(minTier?: string, maxTier?: string) {
    const minLabel = tierLabel(minTier);
    const maxLabel = tierLabel(maxTier || minTier);
    if (minLabel === maxLabel) return `Печать от: ${minLabel}`;
    return `Печать от: ${minLabel} до ${maxLabel}`;
  }

  const tierLevelMap: Record<string, number> = {
    player: 0,
    gamer: 1,
    skilled: 2,
    expert: 3,
    hero: 4,
    king: 5,
    legend: 6,
  };
  const tierByLevel = ["player", "gamer", "skilled", "expert", "hero", "king", "legend"] as const;

  function tierFromLevel(level: number): (typeof tierByLevel)[number] {
    const n = Number(level);
    const idx = Number.isFinite(n) ? Math.max(0, Math.min(tierByLevel.length - 1, Math.floor(n))) : 0;
    return tierByLevel[idx];
  }

  function extractTags(text: string): string[] {
    const src = String(text || "");
    const out = new Set<string>();
    const re = /#([a-zA-Zа-яА-Я0-9_]+)/g;
    let m: RegExpExecArray | null = null;
    while ((m = re.exec(src)) !== null) out.add(m[1].toLowerCase());
    return Array.from(out);
  }

  const filteredFeedPosts = useMemo(() => {
    const tagNeedle = feedTagFilter.trim().toLowerCase().replace(/^#/, "");
    return posts.filter((p) => {
      if (feedTierFilters.length) {
        const lvl = tierLevelMap[String(p.requiredTier || "player").toLowerCase()] ?? 0;
        const ok = feedTierFilters.some((tier) => (tierLevelMap[tier] ?? -1) === lvl);
        if (!ok) return false;
      }
      if (feedMaxActions !== "" && typeof p.codeActions === "number" && p.codeActions > Number(feedMaxActions)) {
        return false;
      }
      if (tagNeedle) {
        const tags = extractTags(`${p.title || ""}\n${p.description || ""}`);
        if (!tags.includes(tagNeedle)) return false;
      }
      return true;
    });
  }, [posts, feedTierFilters, feedMaxActions, feedTagFilter]);

  function postCardSx() {
    const isLight = theme.palette.mode === "light";
    return {
      height: "100%",
      borderColor: isLight ? "rgba(37, 99, 235, 0.22)" : "rgba(255,255,255,0.12)",
      background: isLight
        ? `linear-gradient(180deg, ${adminPostCardLightFrom} 0%, ${adminPostCardLightTo} 100%)`
        : "linear-gradient(180deg, rgba(20,27,36,0.96) 0%, rgba(15,20,26,0.96) 100%)",
      boxShadow: isLight ? "0 6px 20px rgba(37,99,235,0.10)" : "0 6px 20px rgba(0,0,0,0.28)",
      transition: "transform .16s ease, box-shadow .16s ease, border-color .16s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: isLight ? "0 10px 28px rgba(37,99,235,0.16)" : "0 10px 28px rgba(0,0,0,0.38)",
        borderColor: isLight ? "rgba(37,99,235,0.36)" : "rgba(255,255,255,0.2)",
      },
    } as const;
  }

  function parseTs(v: unknown): number | null {
    if (typeof v === "number") return Number.isFinite(v) ? (v < 1_000_000_000_000 ? v * 1000 : v) : null;
    if (typeof v === "string" && v.trim()) {
      const s = v.trim();
      const n = Number(s);
      if (Number.isFinite(n)) return n < 1_000_000_000_000 ? n * 1000 : n;
      const parsed = Date.parse(s);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  function fmtDate(v: unknown): string {
    const ts = parseTs(v);
    if (!ts) return "—";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  }

  function tierChipSx(tier?: string) {
    const dark = theme.palette.mode === "dark";
    const key = String(tier || "player").toLowerCase();
    const t = tierPalette[key] || tierPalette.player;
    return {
      background: dark ? t.bgDark || t.bg : t.bg,
      color: dark ? t.fgDark || t.fg : t.fg,
      border: `1px solid ${dark ? t.borderDark || t.border : t.border}`,
      fontWeight: 700,
    } as const;
  }

  function tierRangeChipSx(minTier?: string, maxTier?: string) {
    const dark = theme.palette.mode === "dark";
    const minKey = String(minTier || "player").toLowerCase();
    const min = tierPalette[minKey] || tierPalette.player;
    const minBorder = dark ? min.borderDark || min.border : min.border;
    const baseBg = dark ? "rgba(20,27,36,0.86)" : "rgba(255,255,255,0.86)";
    const baseFg = dark ? min.fgDark || min.fg : min.fg;
    return {
      background: `linear-gradient(${baseBg}, ${baseBg}) padding-box, linear-gradient(${minBorder}, ${minBorder}) border-box`,
      border: "1px solid transparent",
      color: baseFg,
      fontWeight: 700,
    } as const;
  }

  function tierFilterChipSx(tier: string, active: boolean) {
    if (active) {
      return {
        ...tierChipSx(tier),
        "& .MuiChip-label": { px: 1.1 },
      } as const;
    }
    return {
      background: "transparent",
      color: "text.primary",
      border: "1px solid",
      borderColor: "divider",
      fontWeight: 600,
      boxShadow: "none",
      position: "relative",
      transform: "none",
      transition: "all 140ms ease",
      "& .MuiChip-label": { px: 1.1 },
    } as const;
  }

  function isVoteLocked(postId?: string): boolean {
    if (!postId) return false;
    return (voteLockUntil[postId] || 0) > Date.now();
  }

  async function runWithVoteLock(postId: string, task: () => Promise<void>) {
    const lockUntil = Date.now() + 5000;
    setVoteLockUntil((prev) => ({ ...prev, [postId]: lockUntil }));
    const timer = window.setTimeout(() => {
      setVoteLockUntil((prev) => {
        if ((prev[postId] || 0) !== lockUntil) return prev;
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    }, 5000);
    try {
      await task();
    } finally {
      window.clearTimeout(timer);
      setVoteLockUntil((prev) => {
        if ((prev[postId] || 0) !== lockUntil) return prev;
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    }
  }

  function authorPill({ username, avatar, score, nameColor, onClick }: { username: string; avatar?: string; score?: number; nameColor?: string; onClick?: () => void }) {
    return (
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 999,
          px: 1,
          py: 0.35,
          maxWidth: "100%",
          cursor: onClick ? "pointer" : "default",
          userSelect: "none",
          bgcolor: "background.paper",
        }}
      >
        <Avatar src={avatar} imgProps={{ referrerPolicy: "no-referrer" }} sx={{ width: 22, height: 22 }}>
          {username.slice(0, 1)}
        </Avatar>
        <Typography variant="body2" fontWeight={800} noWrap sx={{ maxWidth: 170 }}>
          <span style={{ color: nameColor || "inherit" }}>{username}</span>
        </Typography>
        {typeof score === "number" ? (
          <Box
            sx={{
              ...scoreChipSx(score),
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              borderRadius: 999,
              px: 0.9,
              py: 0.2,
              fontSize: 12,
              fontWeight: 900,
              lineHeight: 1.1,
            }}
          >
            <FavoriteRoundedIcon sx={{ fontSize: 14, color: "inherit" }} />
            {score}
          </Box>
        ) : null}
      </Box>
    );
  }

  function postStatsRow(p: PostListItem) {
    const hasAny =
      typeof (p as any).configsCount === "number" ||
      typeof p.codeEvents === "number" ||
      typeof p.codeFuncs === "number" ||
      typeof p.codeLoops === "number" ||
      typeof p.codeActions === "number" ||
      typeof p.codeLines === "number";
    if (!hasAny) return null;

    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.25 }}>
        {typeof (p as any).configsCount === "number" ? <Chip size="small" variant="outlined" label={`изданий: ${(p as any).configsCount}`} /> : null}
        {typeof p.codeEvents === "number" ? <Chip size="small" variant="outlined" label={`событий: ${p.codeEvents}`} /> : null}
        {typeof p.codeFuncs === "number" ? <Chip size="small" variant="outlined" label={`функций: ${p.codeFuncs}`} /> : null}
        {typeof p.codeLoops === "number" ? <Chip size="small" variant="outlined" label={`циклов: ${p.codeLoops}`} /> : null}
        {typeof p.codeActions === "number" ? <Chip size="small" variant="outlined" label={`действий: ${p.codeActions}`} /> : null}
        {typeof p.codeLines === "number" ? <Chip size="small" variant="outlined" label={`строк: ${p.codeLines}`} /> : null}
      </Stack>
    );
  }

  const safeDescHtml = useMemo(() => {
    const raw = marked.parse(pubDesc || "", { breaks: true }) as string;
    return DOMPurify.sanitize(raw);
  }, [pubDesc]);

  const safePostDescHtml = useMemo(() => {
    const raw = marked.parse(post?.description || "", { breaks: true }) as string;
    return DOMPurify.sanitize(raw);
  }, [post?.description]);

  async function runUserSearch(q: string) {
    const qq = (q || "").trim();
    setSearchQ(q);
    if (qq.length < 2) {
      setSearchItems([]);
      return;
    }
    setSearchLoading(true);
    try {
      const r = await api.usersSearch(qq);
      if ("error" in r) throw new Error(mapApiError(r.error));
      setSearchItems(r.items || []);
    } finally {
      setSearchLoading(false);
    }
  }

  async function runAdminUsersSearch(q: string) {
    const qq = (q || "").trim();
    setAdminUsersQ(q);
    setAdminUsersLoading(true);
    try {
      const r = await api.adminUsers(qq);
      if ("error" in r) throw new Error(mapApiError(r.error));
      setAdminUsersItems((r.items || []) as any);
    } finally {
      setAdminUsersLoading(false);
    }
  }

  function parsePostFiles(code: string) {
    const text = String(code || "");
    const re = /^# --- file: (.+?) \((\d+) B\) ---\s*$/;
    const lines = text.split(/\r?\n/);
    const files: Array<{ name: string; size: number; text: string }> = [];
    let curName: string | null = null;
    let curSize = 0;
    let buf: string[] = [];

    function flush() {
      if (!curName) return;
      const body = buf.join("\n").trim() + "\n";
      files.push({ name: curName, size: curSize || byteLenUtf8(body), text: body });
      curName = null;
      curSize = 0;
      buf = [];
    }

    for (const line of lines) {
      const m = re.exec(line);
      if (m) {
        flush();
        curName = m[1].trim();
        curSize = Number(m[2] || 0) || 0;
        continue;
      }
      if (curName) buf.push(line);
    }
    flush();

    if (!files.length) {
      return [{ name: "code.mldsl", size: byteLenUtf8(text), text }];
    }
    return files;
  }

  function quoteCmdArg(v: string) {
    const s = String(v ?? "");
    if (!s) return "\"\"";
    if (/[\s"]/g.test(s)) return `"${s.replace(/"/g, '\\"')}"`;
    return s;
  }

  async function loadPublishFromPost(p: PostDetail) {
    const cfgsRaw = Array.isArray((p as any)?.configs) && (p as any).configs.length ? (p as any).configs : null;
    const mode = ((p as any)?.versionMode === "global" ? "global" : "per_edition") as VersionMode;
    if (cfgsRaw) {
      const byId = new Map<string, string>();
      const activeId = String((p as any)?.activeConfigId || cfgsRaw[0]?.id || "default");
      for (const cfg of cfgsRaw) {
        const id = normalizeEditionKey(String(cfg?.id || ""));
        const r = await api.get(String(p.id || ""), id);
        if ("error" in r) {
          setErr(mapApiError(r.error));
          return;
        }
        byId.set(id, String((r as any)?.effectiveCode || ""));
      }
      const mapped: PublishEdition[] = cfgsRaw.slice(0, MAX_EDITIONS).map((c: any, idx: number) => {
        const id = normalizeEditionKey(String(c?.id || c?.title || `edition_${idx + 1}`));
        const code = String(byId.get(id) || "");
        const files = parsePostFiles(code);
        return {
          id,
          title: String(c?.title || id),
          description: String(c?.description || ""),
          code,
          files,
        };
      });
      setPubEditions(mapped.length ? mapped : [{ id: "default", title: "Базовое", description: "", code: p.code || "", files: parsePostFiles(p.code || "") }]);
      setPubActiveEditionId(mapped.some((x) => x.id === activeId) ? activeId : mapped[0]?.id || "default");
    } else {
      const code = String(p.code || "");
      setPubEditions([{ id: "default", title: "Базовое", description: "", code, files: parsePostFiles(code) }]);
      setPubActiveEditionId("default");
    }
    setPubVersionMode(mode);
    setPubInitialVersionMode(mode);
    setPubTitle(String(p.title || ""));
    setPubDesc(String(p.description || ""));
    setEditPostId(String(p.id || ""));
    setEditReturnPostId(String(p.id || ""));
    setTab("publish");
    setPostOpen(false);
  }

  useEffect(() => {
    if (!post?.code) {
      setCodeFiles([]);
      setCodeActiveIdx(-1);
      return;
    }
    const files = parsePostFiles(post.code);
    console.log("[post-debug] codeFiles_sync", {
      postId: String(post?.id || ""),
      activeConfigId: String(postActiveConfigId || (post as any)?.activeConfigId || ""),
      codeLen: Number(String(post?.code || "").length || 0),
      filesCount: Number(files.length || 0),
      fileNames: files.map((f) => String(f.name || "")),
      tier: String((post as any)?.requiredTier || ""),
      tierLevel: Number((post as any)?.requiredTierLevel ?? -1),
    });
    setCodeFiles(files);
    setCodeActiveIdx(files.length === 1 ? 0 : -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id, post?.code, postActiveConfigId]);

  useEffect(() => {
    if (!post) {
      setInlineEdit(false);
      setInlineTitle("");
      setInlineDesc("");
      setInlineCode("");
      setInlineFiles([]);
      return;
    }
    setInlineEdit(false);
    setInlineTitle(post.title || "");
    setInlineDesc(post.description || "");
    setInlineCode(post.code || "");
    setInlineFiles(parsePostFiles(post.code || ""));
  }, [post?.id, post?.code, postActiveConfigId]);

  useEffect(() => {
    if (!postVersionsOpen || !post) return;
    void openPostVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postVersionsShowAll]);

  useEffect(() => {
    if (editPostId) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (!d || typeof d !== "object") return;
      if (typeof d.title === "string") setPubTitle(d.title);
      if (typeof d.description === "string") setPubDesc(d.description);
      if (d.versionMode === "per_edition" || d.versionMode === "global") {
        setPubVersionMode(d.versionMode);
        setPubInitialVersionMode(d.versionMode);
      }
      if (Array.isArray(d.editions) && d.editions.length) {
        const normalized: PublishEdition[] = d.editions
          .map((x: any, idx: number) => {
            const id = normalizeEditionKey(String(x?.id || x?.title || `edition_${idx + 1}`));
            const title = String(x?.title || `Издание ${idx + 1}`);
            const code = String(x?.code || "");
            const files = Array.isArray(x?.files) ? x.files : parsePostFiles(code);
            return {
              id,
              title,
              description: String(x?.description || ""),
              code,
              files: files.map((f: any) => ({ name: String(f?.name || "code.mldsl"), size: Number(f?.size || 0), text: String(f?.text || "") })),
            };
          })
          .slice(0, MAX_EDITIONS);
        if (normalized.length) {
          setPubEditions(normalized);
          const active = normalizeEditionKey(String(d.activeEditionId || normalized[0].id));
          setPubActiveEditionId(normalized.some((x) => x.id === active) ? active : normalized[0].id);
        }
      } else if (typeof d.code === "string") {
        const files = parsePostFiles(d.code);
        setPubEditions([{ id: "default", title: "Базовое", description: "", code: d.code, files }]);
        setPubActiveEditionId("default");
      } else {
        setPubEditions([{ id: "default", title: "Базовое", description: "", code: "", files: [] }]);
        setPubActiveEditionId("default");
      }
      setDraftStatus("черновик восстановлен");
      window.setTimeout(() => setDraftStatus(""), 1400);
    } catch {
      // ignore broken local draft
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editPostId) return;
    const payload = JSON.stringify({
      title: pubTitle,
      description: pubDesc,
      versionMode: pubVersionMode,
      activeEditionId: pubActiveEditionId,
      editions: pubEditions.map((e) => ({
        id: e.id,
        title: e.title,
        description: e.description || "",
        code: e.code || "",
        files: e.files || [],
      })),
      updatedAt: Date.now(),
    });
    localStorage.setItem(draftKey, payload);
  }, [draftKey, editPostId, pubTitle, pubDesc, pubVersionMode, pubActiveEditionId, pubEditions]);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backdropFilter: "blur(10px)",
          bgcolor: me?.topbarColor ? me.topbarColor : undefined,
          backgroundImage: me?.topbarColor ? "none" : undefined,
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={900} noWrap>
              MLDSL Hub
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Каталог кодов MLDSL. Автомодерация основана на лайках/репортах — перед использованием проверь отзывы и риск‑флаги.
            </Typography>
          </Box>

          <Tooltip title="Тема">
            <IconButton onClick={onToggleMode} color="inherit">
              <ContrastRoundedIcon />
            </IconButton>
          </Tooltip>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: mdUp ? 320 : 180 }}>
            <TextField
              size="small"
              placeholder="Поиск постов"
              value={feedQueryInput}
              onChange={(e) => setFeedQueryInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                setTab("feed");
                setFeedQuery(feedQueryInput.trim());
              }}
              sx={{
                flex: 1,
                "& .MuiInputBase-root": { bgcolor: "background.paper" },
              }}
            />
            <Tooltip title="Искать посты">
              <IconButton
                color="inherit"
                onClick={() => {
                  setTab("feed");
                  setFeedQuery(feedQueryInput.trim());
                }}
              >
                <SearchRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Поиск пользователей">
              <IconButton
                color="inherit"
                onClick={() => {
                  setSearchOpen(true);
                  setSearchQ("");
                  setSearchItems([]);
                }}
              >
                <PersonRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          {discordUrl ? (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Tooltip title="Техподдержка Discord">
                <IconButton component="a" href={discordUrl} target="_blank" rel="noreferrer" color="inherit">
                  <DiscordLogoIcon />
                </IconButton>
              </Tooltip>
              <Typography
                component="a"
                href={discordUrl}
                target="_blank"
                rel="noreferrer"
                variant="body2"
                fontWeight={700}
                noWrap
                sx={{ color: "inherit", textDecoration: "none", cursor: "pointer" }}
              >
                Техподдержка
              </Typography>
            </Stack>
          ) : null}

          {me ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                src={me.avatar}
                sx={{ width: 28, height: 28, cursor: "pointer" }}
                onClick={() => setTab("profile")}
              >
                {me.username.slice(0, 1)}
              </Avatar>
              <Typography
                variant="body2"
                fontWeight={700}
                noWrap
                sx={{ maxWidth: 180, cursor: "pointer" }}
                onClick={() => setTab("profile")}
              >
                {me.username}
              </Typography>
              <Tooltip title="Твой рейтинг: сумма лайков всех постов (градиент: -5 красный, 25 зелёный, 100 голубой)">
                <Chip
                  size="small"
                  icon={<FavoriteRoundedIcon sx={{ color: "rgba(255,255,255,0.92) !important" }} />}
                  label={String(myScore)}
                  sx={myScoreSx}
                />
              </Tooltip>
              <Tooltip title="Выйти">
                <IconButton
                  color="inherit"
                  onClick={async () => {
                    await api.logout();
                    location.reload();
                  }}
                >
                  <LogoutRoundedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Button variant="contained" startIcon={<LoginRoundedIcon />} onClick={() => setLoginOpen(true)}>
              Войти
            </Button>
          )}
        </Toolbar>
        <Divider />
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="inherit">
            <Tab value="feed" icon={<HomeRoundedIcon />} iconPosition="start" label="Посты" />
            <Tab value="favorites" icon={<StarRoundedIcon />} iconPosition="start" label="Избранное" disabled={!me} />
            <Tab value="publish" icon={<PublishRoundedIcon />} iconPosition="start" label="Опубликовать" />
            <Tab value="profile" icon={<PersonRoundedIcon />} iconPosition="start" label="Профиль" disabled={!me} />
            <Tab value="download" icon={<DownloadRoundedIcon />} iconPosition="start" label="Скачать MLDSL" />
            <Tab value="help" icon={<HelpOutlineRoundedIcon />} iconPosition="start" label="Помощь" />
          </Tabs>
          <Box sx={{ flex: 1 }} />
          {loading ? <Typography variant="body2">Загрузка…</Typography> : null}
        </Toolbar>
      </AppBar>

      <Dialog open={loginOpen} onClose={() => setLoginOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Войти</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Выбери способ входа:
          </Typography>
          <Stack spacing={1.25} sx={{ mt: 2 }}>
            <Button variant="contained" href={loginUrlGoogle} startIcon={<LoginRoundedIcon />}>
              Google
            </Button>
            <Button variant="outlined" href={loginUrlDiscord}>
              Discord
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginOpen(false)}>Отмена</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pubModeConfirmOpen} onClose={() => setPubModeConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Сменить режим версий?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Режим изменится с <b>{pubVersionMode}</b> на <b>{pubPendingMode || pubVersionMode}</b>. При сохранении сервер выполнит миграцию истории.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPubModeConfirmOpen(false); setPubPendingMode(null); }}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pubPendingMode) setPubVersionMode(pubPendingMode);
              setPubModeConfirmOpen(false);
              setPubPendingMode(null);
            }}
          >
            Применить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={searchOpen} onClose={() => setSearchOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Поиск пользователей</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Ник"
            value={searchQ}
            onChange={(e) => runUserSearch(e.target.value)}
            placeholder="Например: Universe"
            sx={{ mt: 1 }}
          />
          <Stack spacing={1} sx={{ mt: 2 }}>
            {searchLoading ? <Typography color="text.secondary">Поиск…</Typography> : null}
            {!searchLoading && !searchItems.length && searchQ.trim().length >= 2 ? (
              <Typography color="text.secondary">Ничего не найдено.</Typography>
            ) : null}
            {searchItems.map((u) => (
              <Card key={u.id} variant="outlined">
                <CardActionArea
                  onClick={() => {
                    setSearchOpen(false);
                    openUser(u.id).catch((e) => setErr(String(e?.message || e)));
                  }}
                >
                  <CardContent>{authorPill({ username: u.username, avatar: u.avatar, score: u.totalScore ?? 0, nameColor: u.nameColor })}</CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={adminUsersOpen}
        onClose={() => {
          setAdminUsersOpen(false);
          setAdminBanOpen(false);
          setAdminDeleteOpen(false);
          setAdminBanUser(null);
          setAdminDeleteUser(null);
          setAdminBanReason("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Админ: пользователи</DialogTitle>
        <DialogContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Поиск"
              value={adminUsersQ}
              onChange={(e) => setAdminUsersQ(e.target.value)}
              placeholder="username или email"
            />
            <Button
              variant="outlined"
              onClick={() => runAdminUsersSearch(adminUsersQ).catch((e) => setErr(String(e?.message || e)))}
              disabled={adminUsersLoading}
              sx={{ whiteSpace: "nowrap" }}
            >
              Найти
            </Button>
          </Stack>

          <Stack spacing={1} sx={{ mt: 2 }}>
            {adminUsersLoading ? <Typography color="text.secondary">Загрузка…</Typography> : null}
            {!adminUsersLoading && !adminUsersItems.length ? (
              <Typography color="text.secondary">Нет результатов.</Typography>
            ) : null}

            {adminUsersItems.map((u) => {
              const banned = !!u.banned;
              const score = typeof u.totalScore === "number" ? u.totalScore : 0;
              return (
                <Card key={u.id} variant="outlined">
                  <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                      {authorPill({ username: u.username, avatar: u.avatar, score, nameColor: u.nameColor })}
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        {banned ? <Chip size="small" color="error" variant="outlined" label="BAN" /> : null}
                        <Button
                          size="small"
                          variant={banned ? "outlined" : "contained"}
                          color={banned ? "inherit" : "error"}
                          onClick={() => {
                            if (banned) {
                              api
                                .adminBanUser(u.id, { banned: false })
                                .then((r) => {
                                  if ("error" in r) return setErr(mapApiError(r.error));
                                  runAdminUsersSearch(adminUsersQ).catch(() => null);
                                })
                                .catch((e) => setErr(String(e?.message || e)));
                              return;
                            }
                            setAdminBanUser(u);
                            setAdminBanReason("");
                            setAdminBanOpen(true);
                          }}
                        >
                          {banned ? "Разбанить" : "Забанить"}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setAdminDeleteUser(u);
                            setAdminDeleteOpen(true);
                          }}
                        >
                          Удалить
                        </Button>
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip size="small" variant="outlined" label={`id: ${u.id}`} />
                      {u.email ? <Chip size="small" variant="outlined" label={`email: ${u.email}`} /> : null}
                      {u.discordId ? <Chip size="small" variant="outlined" label={`discord: ${u.discordId}`} /> : null}
                      {u.googleId ? <Chip size="small" variant="outlined" label={`google: ${u.googleId}`} /> : null}
                      {banned && u.bannedReason ? <Chip size="small" color="error" variant="outlined" label={`reason: ${u.bannedReason}`} /> : null}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminUsersOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={adminBanOpen} onClose={() => setAdminBanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Бан пользователя</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {adminBanUser ? `Пользователь: ${adminBanUser.username}` : ""}
          </Typography>
          <TextField
            fullWidth
            label="Причина (опционально)"
            value={adminBanReason}
            onChange={(e) => setAdminBanReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminBanOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!adminBanUser}
            onClick={async () => {
              if (!adminBanUser) return;
              const r = await api.adminBanUser(adminBanUser.id, { banned: true, reason: adminBanReason.trim() || undefined });
              if ("error" in r) return setErr(mapApiError(r.error));
              setAdminBanOpen(false);
              setAdminBanUser(null);
              setAdminBanReason("");
              await runAdminUsersSearch(adminUsersQ).catch(() => null);
            }}
          >
            Забанить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={adminDeleteOpen} onClose={() => setAdminDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Удалить аккаунт</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {adminDeleteUser ? `Будут удалены пользователь и все его посты/версии/комментарии/голоса/репорты/сессии.` : ""}
          </Typography>
          <Typography sx={{ mt: 1.25 }}>
            {adminDeleteUser ? `Удалить: ${adminDeleteUser.username} (${adminDeleteUser.id})?` : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminDeleteOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!adminDeleteUser}
            onClick={async () => {
              if (!adminDeleteUser) return;
              const r = await api.adminDeleteUser(adminDeleteUser.id);
              if ("error" in r) return setErr(mapApiError(r.error));
              setAdminDeleteOpen(false);
              setAdminDeleteUser(null);
              await runAdminUsersSearch(adminUsersQ).catch(() => null);
            }}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!err}
        onClose={() => setErr("")}
        autoHideDuration={9000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: theme.zIndex.modal + 1 }}
      >
        <Alert onClose={() => setErr("")} severity="error" variant="filled" sx={{ whiteSpace: "pre-line" }}>
          {err}
        </Alert>
      </Snackbar>

      <Container maxWidth={false} sx={{ py: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {tab === "download" ? (
              <Card>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Typography fontWeight={900}>Скачать MLDSL и BetterCode</Typography>
                    <Typography color="text.secondary">
                      Основной установщик MLDSL ставит компилятор, расширение VS Code и совместимый мод BetterCode.
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="contained"
                        startIcon={<DownloadRoundedIcon />}
                        href="https://github.com/rainbownyashka/mldsl/releases/latest"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Скачать MLDSL (latest)
                      </Button>
                      <Button
                        variant="outlined"
                        href="https://github.com/rainbownyashka/mlbettercode/releases/latest"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Скачать BetterCode (latest)
                      </Button>
                    </Stack>
                    <Divider />
                    <Typography color="text.secondary">
                      Для разработчиков и документации:
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        size="small"
                        variant="text"
                        href="https://github.com/rainbownyashka/mldsl"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Репозиторий MLDSL
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        href="https://github.com/rainbownyashka/mlbettercode"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Репозиторий BetterCode
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ) : tab === "help" ? (
              <Card>
                <CardContent>
                  <Stack spacing={1.25}>
                    <Typography fontWeight={900}>Помощь</Typography>
                    <Typography color="text.secondary">
                      MLDSL Hub — это «pip install» для кода на MLDSL: публикуешь готовые модули и подключаешь их в игре одной командой.
                    </Typography>

                    <Card variant="outlined">
                      <CardContent>
                        <Typography fontWeight={900}>Быстрый старт</Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                          1) Открой пост и нажми «Распечатать».
                          <br />
                          2) В Minecraft введи команду из кнопки — мод сам скачает `plan.json` с MLDSL Hub (кэш: `.minecraft/mldsl_modules/...`).
                          <br />
                          3) Для старых сборок можно использовать fallback: `/loadmodule &lt;id_поста&gt; plan.json`, затем `/confirmload`.
                          <br />
                          4) Можно просто скачать и редактировать файлы локально — затем печатать через `/mldsl run` (если у тебя есть `plan.json`) или через компилятор.
                        </Typography>
                      </CardContent>
                    </Card>

                    {discordUrl ? (
                      <Card variant="outlined">
                        <CardContent>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                            <Typography color="text.secondary">Нужна помощь или баг-репорт?</Typography>
                            <Button
                              component="a"
                              href={discordUrl}
                              target="_blank"
                              rel="noreferrer"
                              variant="contained"
                              startIcon={<DiscordLogoIcon />}
                            >
                              Техподдержка
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    ) : null}

                    <Card variant="outlined">
                      <CardContent>
                        <Typography fontWeight={900}>Безопасность</Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                          Автомодерация основана на лайках/репортах. Перед использованием кода проверь отзывы и риск‑флаги.
                        </Typography>
                      </CardContent>
                    </Card>

                    <Card variant="outlined">
                      <CardContent>
                        <Typography fontWeight={900}>Команда</Typography>
                        <Typography component="pre" sx={{ m: 0, mt: 0.5, whiteSpace: "pre-wrap", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12 }}>
                          {`/mldsl run <id_поста> [--config <id_издания>]\n# fallback (legacy):\n/loadmodule <id_поста> plan.json\n/confirmload`}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                          `id_поста` — это ID из адресной строки поста. Для постов с изданиями добавляй `--config`.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Stack>
                </CardContent>
              </Card>
            ) : tab === "publish" ? (
              <Card>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography fontWeight={900}>Опубликовать</Typography>
                      {editPostId ? <Chip size="small" variant="outlined" color="info" label="редактирование" /> : null}
                    </Stack>

                    <TextField label="Название" value={pubTitle} onChange={(e) => setPubTitle(e.target.value)} fullWidth />

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography fontWeight={800}>Режим версий:</Typography>
                      <Chip
                        size="small"
                        clickable
                        color={pubVersionMode === "per_edition" ? "primary" : "default"}
                        variant={pubVersionMode === "per_edition" ? "filled" : "outlined"}
                        label="per_edition"
                        onClick={() => {
                          if (pubVersionMode === "per_edition") return;
                          setPubPendingMode("per_edition");
                          setPubModeConfirmOpen(true);
                        }}
                      />
                      <Chip
                        size="small"
                        clickable
                        color={pubVersionMode === "global" ? "primary" : "default"}
                        variant={pubVersionMode === "global" ? "filled" : "outlined"}
                        label="global"
                        onClick={() => {
                          if (pubVersionMode === "global") return;
                          setPubPendingMode("global");
                          setPubModeConfirmOpen(true);
                        }}
                      />
                    </Stack>

                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.25 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                            <Typography fontWeight={900}>Издания</Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => createEdition(`Издание ${pubEditions.length + 1}`)}
                              disabled={pubEditions.length >= MAX_EDITIONS}
                            >
                              Добавить издание
                            </Button>
                          </Stack>
                          <Stack spacing={0.75}>
                            {pubEditions.map((ed, idx) => {
                              const active = String(ed.id) === String(pubActiveEditionId);
                              return (
                                <Box
                                  key={ed.id}
                                  sx={{
                                    borderRadius: 1.5,
                                    px: 1,
                                    py: 0.75,
                                    backgroundColor: active ? "action.selected" : "transparent",
                                  }}
                                >
                                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Chip
                                      size="small"
                                      clickable
                                      color={active ? "primary" : "default"}
                                      variant={active ? "filled" : "outlined"}
                                      label={ed.title || ed.id}
                                      onClick={() => setPubActiveEditionId(ed.id)}
                                    />
                                    <Stack direction="row" spacing={0.5}>
                                      <Button size="small" variant="text" onClick={() => moveEdition(ed.id, -1)} disabled={idx === 0}>
                                        ↑
                                      </Button>
                                      <Button size="small" variant="text" onClick={() => moveEdition(ed.id, 1)} disabled={idx === pubEditions.length - 1}>
                                        ↓
                                      </Button>
                                      <Button
                                        size="small"
                                        color="error"
                                        variant="text"
                                        onClick={() => removeEdition(ed.id)}
                                        disabled={pubEditions.length <= 1}
                                      >
                                        ×
                                      </Button>
                                    </Stack>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Stack>

                          <Stack direction="row" spacing={1}>
                            <TextField
                              label="ID издания"
                              value={pubActiveEdition.id}
                              disabled
                              fullWidth
                            />
                            <TextField
                              label="Название издания"
                              value={pubActiveEdition.title}
                              onChange={(e) => updateActiveEdition({ title: e.target.value })}
                              fullWidth
                            />
                          </Stack>
                          <TextField
                            label="Описание издания"
                            value={pubActiveEdition.description || ""}
                            onChange={(e) => updateActiveEdition({ description: e.target.value })}
                            fullWidth
                          />
                        </Stack>
                      </CardContent>
                    </Card>

                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                        <Typography fontWeight={800}>Описание</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Button size="small" variant={descPreview ? "outlined" : "contained"} onClick={() => setDescPreview(false)}>
                            Редактор
                          </Button>
                          <Button size="small" variant={descPreview ? "contained" : "outlined"} onClick={() => setDescPreview(true)}>
                            Preview
                          </Button>
                        </Stack>
                      </Stack>
                      {!descPreview ? (
                        <TextField
                          label="Описание (Markdown)"
                          value={pubDesc}
                          onChange={(e) => setPubDesc(e.target.value)}
                          fullWidth
                          multiline
                          minRows={5}
                          helperText={`Можно Markdown. Enter = перенос строки. Размер: ${descBytes} B`}
                        />
                      ) : (
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            p: 1.5,
                            bgcolor: "background.paper",
                          }}
                          dangerouslySetInnerHTML={{ __html: safeDescHtml || "<em>Пусто</em>" }}
                        />
                      )}
                    </Stack>

                    <Box
                      onDragOver={(e) => {
                        if (!me) return;
                        e.preventDefault();
                        setPubDropActive(true);
                      }}
                      onDragLeave={() => setPubDropActive(false)}
                      onDrop={async (e) => {
                        if (!me) return;
                        e.preventDefault();
                        setPubDropActive(false);
                        await appendPublishFiles(Array.from(e.dataTransfer?.files || []));
                      }}
                      sx={{
                        width: 260,
                        minHeight: 180,
                        borderRadius: 2,
                        border: "2px dashed",
                        borderColor: pubDropActive ? "primary.main" : "divider",
                        bgcolor: pubDropActive ? "action.hover" : "background.paper",
                        transition: "all .15s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        px: 2,
                      }}
                    >
                      <Button
                        variant="text"
                        component="label"
                        disabled={!me}
                        sx={{ width: "100%", height: "100%", py: 2, color: "text.primary" }}
                      >
                        <Stack spacing={1} alignItems="center">
                          <UploadFileRoundedIcon fontSize="large" />
                          <Typography fontWeight={700}>Загрузить файлы</Typography>
                          <Typography variant="body2" color="text.secondary">
                            .mldsl / .json
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            или перетащи файлы
                          </Typography>
                        </Stack>
                        <input
                          type="file"
                          hidden
                          multiple
                          accept=".mldsl,.json,text/plain,application/json"
                          onChange={async (e) => {
                            await appendPublishFiles(Array.from(e.target.files || []));
                          }}
                        />
                      </Button>
                    </Box>

                    {pubActiveEdition.files.length ? (
                      <Stack spacing={1}>
                        <Typography fontWeight={800}>Файлы</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {pubActiveEdition.files.map((f, i) => (
                            <Chip
                              key={`${f.name}-${i}`}
                              icon={<InsertDriveFileRoundedIcon />}
                              label={`${f.name} (${f.size} B)`}
                              onDelete={() => {
                                const next = pubActiveEdition.files.filter((_, j) => j !== i);
                                updateActiveEdition({ files: next, code: buildBundleFromFiles(next) });
                              }}
                              deleteIcon={<DeleteRoundedIcon />}
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                        <Typography variant="body2" color={codeBytes > MAX_CODE_BYTES ? "error" : "text.secondary"}>
                          Код будет собран из файлов. Размер: {codeBytes} B (лимит {MAX_CODE_BYTES} B)
                        </Typography>
                        {!publishHasPlan ? (
                          <Typography variant="body2" color="error">
                            Нужен `plan.json` (иначе команда `/mldsl run` не сможет печатать).
                          </Typography>
                        ) : null}
                        {!publishHasSources ? (
                          <Typography variant="body2" color="error">
                            Нужны исходники `.mldsl` (чтобы можно было читать и редактировать модуль).
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Добавь хотя бы один файл (`.mldsl` или `plan.json`).
                      </Typography>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        variant="contained"
                        startIcon={<PublishRoundedIcon />}
                        disabled={
                          !me ||
                          publishBusy ||
                          !pubTitle.trim() ||
                          pubEditions.some((e) => !String(e.code || "").trim()) ||
                          codeBytes > MAX_CODE_BYTES ||
                          !pubActiveEdition.files.length ||
                          !publishHasPlan ||
                          !publishHasSources
                        }
                        onClick={async () => {
                          if (publishBusy) return;
                          setErr("");
                          setPubStatus("…");
                          setPublishBusy(true);
                          try {
                            const payloadConfigs = pubEditions.map((e) => ({
                              id: e.id,
                              title: e.title || e.id,
                              description: e.description || "",
                              code: e.code || "",
                            }));
                            const r = editPostId
                              ? await api.updatePost(editPostId, {
                                  title: pubTitle,
                                  description: pubDesc,
                                  activeConfigId: pubActiveEdition.id,
                                  configs: payloadConfigs,
                                  versionMode: pubVersionMode,
                                  ...(pubInitialVersionMode !== pubVersionMode ? { newVersionMode: pubVersionMode } : {}),
                                })
                              : await api.publish({
                                  title: pubTitle,
                                  description: pubDesc,
                                  activeConfigId: pubActiveEdition.id,
                                  versionMode: pubVersionMode,
                                  configs: payloadConfigs,
                                });
                            if ("error" in r) {
                              setPubStatus("");
                              setErr(mapApiError(r.error));
                              return;
                            }
                            setPubStatus(editPostId ? "сохранено" : "опубликовано");
                            setPubTitle("");
                            setPubDesc("");
                            setPubVersionMode("per_edition");
                            setPubInitialVersionMode("per_edition");
                            setPubEditions([{ id: "default", title: "Базовое", description: "", code: "", files: [] }]);
                            setPubActiveEditionId("default");
                            setEditPostId(null);
                            setEditReturnPostId(null);
                            await refreshFeed();
                          } finally {
                            setPublishBusy(false);
                          }
                        }}
                      >
                        {publishBusy ? (editPostId ? "Сохранение..." : "Публикация...") : editPostId ? "Сохранить" : "Опубликовать"}
                      </Button>
                      {editPostId ? (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setEditPostId(null);
                            const ret = editReturnPostId;
                            setEditReturnPostId(null);
                            setPubTitle("");
                            setPubDesc("");
                            setPubVersionMode("per_edition");
                            setPubInitialVersionMode("per_edition");
                            setPubEditions([{ id: "default", title: "Базовое", description: "", code: "", files: [] }]);
                            setPubActiveEditionId("default");
                            setPubStatus("");
                            if (ret) {
                              setTab("feed");
                              openPost(ret).catch((e) => setErr(String(e?.message || e)));
                            }
                          }}
                        >
                          Отменить
                        </Button>
                      ) : null}
                      <Typography variant="body2" color="text.secondary">
                        {pubStatus}
                      </Typography>
                      {!editPostId ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => {
                            localStorage.removeItem(draftKey);
                            setDraftStatus("черновик очищен");
                            window.setTimeout(() => setDraftStatus(""), 1200);
                          }}
                        >
                          Очистить черновик
                        </Button>
                      ) : null}
                      {draftStatus ? (
                        <Typography variant="body2" color="text.secondary">
                          {draftStatus}
                        </Typography>
                      ) : null}
                    </Stack>
                    {!me ? (
                      <Typography variant="body2" color="text.secondary">
                        Войти нужно, чтобы публиковать/лайкать/комментировать.
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            ) : null}
          </Grid>

          {tab === "feed" ? (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
                    <Typography fontWeight={900}>Посты</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Открывай пост, чтобы видеть код, флаги и комментарии
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Chip
                      clickable
                      color={feedSort === "latest" ? "primary" : "default"}
                      label="Новые"
                      onClick={() => setFeedSort("latest")}
                    />
                    <Chip
                      clickable
                      color={feedSort === "top" ? "primary" : "default"}
                      label="Топ"
                      onClick={() => setFeedSort("top")}
                    />
                    <Box sx={{ flex: 1 }} />
                    {feedHasMore ? (
                      <Button size="small" variant="outlined" disabled={feedPaging} onClick={() => loadMoreFeed().catch((e) => setErr(String(e?.message || e)))}>
                        {feedPaging ? "Загрузка…" : "Ещё"}
                      </Button>
                    ) : null}
                  </Stack>

                  {feedQuery ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <Chip size="small" color="primary" variant="outlined" label={`Поиск: ${feedQuery}`} />
                      <Button
                        size="small"
                        onClick={() => {
                          setFeedQuery("");
                          setFeedQueryInput("");
                        }}
                      >
                        Сбросить
                      </Button>
                    </Stack>
                  ) : null}

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      size="small"
                      label="Тег (#tag)"
                      value={feedTagFilter}
                      onChange={(e) => setFeedTagFilter(e.target.value)}
                      sx={{ minWidth: 180 }}
                    />
                    <TextField
                      size="small"
                      label="Макс. действий"
                      type="number"
                      value={feedMaxActions}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFeedMaxActions(v === "" ? "" : Math.max(0, Number(v)));
                      }}
                      sx={{ minWidth: 170 }}
                    />
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      <Chip
                        clickable
                        size="small"
                        color="default"
                        label="Все ранги"
                        onClick={() => setFeedTierFilters([])}
                        sx={{
                          border: "1px solid",
                          borderColor: !feedTierFilters.length ? "primary.main" : "divider",
                          fontWeight: !feedTierFilters.length ? 700 : 500,
                          background: !feedTierFilters.length ? "rgba(25,118,210,0.12)" : "transparent",
                        }}
                      />
                      {(["player", "gamer", "skilled", "expert", "hero", "king", "legend"] as const).map((t) => {
                        const active = feedTierFilters.includes(t);
                        return (
                          <Chip
                            key={t}
                            clickable
                            size="small"
                            color="default"
                            label={tierLabel(t)}
                            onClick={() =>
                              setFeedTierFilters((prev) =>
                                prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
                              )
                            }
                            sx={tierFilterChipSx(t, active)}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    {filteredFeedPosts.map((p) => {
                      const r = repLabel(p.score);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={p.id}>
                          <Card variant="outlined" sx={postCardSx()}>
                            <CardActionArea
                              sx={{ height: "100%", alignItems: "stretch" }}
                              onClick={() => openPost(p.id).catch((e) => setErr(String(e?.message || e)))}
                            >
                              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
                                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                  <Typography fontWeight={900} variant="subtitle1" noWrap sx={{ minWidth: 0, flex: 1 }}>
                                    {p.title}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                    {fmtDate(p.created_at)}
                                  </Typography>
                                </Stack>

                                <Typography variant="body2" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                  {p.description || ""}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                  {p.authorUsername
                                    ? authorPill({
                                        username: p.authorUsername,
                                        avatar: p.authorAvatar,
                                        score: typeof p.authorScore === "number" ? p.authorScore : undefined,
                                        nameColor: p.authorNameColor,
                                        onClick: () => {
                                          if (p.authorId) openUser(p.authorId).catch((e) => setErr(String(e?.message || e)));
                                        },
                                      })
                                    : null}
                                </Stack>

                                {postStatsRow(p)}

                                <Box sx={{ flex: 1 }} />

                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <Chip
                                    size="small"
                                    label={tierRangeLabel((p as any).minRequiredTier || p.requiredTier, (p as any).maxRequiredTier || p.requiredTier)}
                                    sx={tierRangeChipSx((p as any).minRequiredTier || p.requiredTier, (p as any).maxRequiredTier || p.requiredTier)}
                                  />
                                  <Chip size="small" variant="outlined" icon={<VerifiedRoundedIcon />} label={r.label} color={r.color} />
                                  <Chip size="small" icon={<FavoriteRoundedIcon sx={{ color: "inherit !important" }} />} label={String(p.score)} sx={scoreChipSx(p.score)} />
                                  {p.hidden ? (
                                    <Chip size="small" color="error" variant="outlined" icon={<VisibilityOffRoundedIcon />} label="скрыто" />
                                  ) : null}
                                  <Box sx={{ flex: 1 }} />
                                  {me ? (
                                    <IconButton
                                      size="small"
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const next = !favoriteIds.has(p.id);
                                        // optimistic
                                        setFavoriteIds((prev) => {
                                          const n = new Set(prev);
                                          if (next) n.add(p.id);
                                          else n.delete(p.id);
                                          return n;
                                        });
                                        setFavorites((prev) =>
                                          next ? [p, ...prev.filter((x) => x.id !== p.id)] : prev.filter((x) => x.id !== p.id)
                                        );

                                        const r = await api.setFavorite(p.id, next);
                                        if ("error" in r) {
                                          await refreshFavorites().catch(() => null);
                                          return setErr(mapApiError(r.error));
                                        }
                                      }}
                                    >
                                      {favoriteIds.has(p.id) ? <StarRoundedIcon /> : <StarBorderRoundedIcon />}
                                    </IconButton>
                                  ) : null}
                                </Stack>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                  {!loading && filteredFeedPosts.length === 0 ? (
                    <Typography sx={{ mt: 1.5 }} color="text.secondary">
                      Ничего не найдено.
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          ) : null}

          {tab === "favorites" ? (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
                    <Typography fontWeight={900}>Избранное</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Твои сохранённые посты
                    </Typography>
                  </Stack>

                  {!favorites.length ? (
                    <Typography sx={{ mt: 1.5 }} color="text.secondary">
                      Пока пусто. Открой пост и нажми ★, или добавь из ленты.
                    </Typography>
                  ) : (
                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                      {favorites.map((p) => {
                        const r = repLabel(p.score);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={p.id}>
                            <Card variant="outlined" sx={postCardSx()}>
                              <CardActionArea
                                sx={{ height: "100%", alignItems: "stretch" }}
                                onClick={() => openPost(p.id).catch((e) => setErr(String(e?.message || e)))}
                              >
                                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
                                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                    <Typography fontWeight={900} variant="subtitle1" noWrap sx={{ minWidth: 0, flex: 1 }}>
                                      {p.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                      {fmtDate(p.created_at)}
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                    {p.description || ""}
                                  </Typography>
                                  {p.authorUsername ? (
                                    authorPill({
                                      username: p.authorUsername,
                                      avatar: p.authorAvatar,
                                      score: typeof p.authorScore === "number" ? p.authorScore : undefined,
                                      nameColor: p.authorNameColor,
                                      onClick: () => {
                                        if (p.authorId) openUser(p.authorId).catch((e) => setErr(String(e?.message || e)));
                                      },
                                    })
                                  ) : null}
                                  {postStatsRow(p)}
                                  <Box sx={{ flex: 1 }} />
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Chip
                                      size="small"
                                      label={tierRangeLabel((p as any).minRequiredTier || p.requiredTier, (p as any).maxRequiredTier || p.requiredTier)}
                                      sx={tierRangeChipSx((p as any).minRequiredTier || p.requiredTier, (p as any).maxRequiredTier || p.requiredTier)}
                                    />
                                    <Chip size="small" variant="outlined" icon={<VerifiedRoundedIcon />} label={r.label} color={r.color} />
                                    <Chip size="small" icon={<FavoriteRoundedIcon sx={{ color: "inherit !important" }} />} label={String(p.score)} sx={scoreChipSx(p.score)} />
                                    <Box sx={{ flex: 1 }} />
                                    <IconButton
                                      size="small"
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const r = await api.setFavorite(p.id, false);
                                        if ("error" in r) return setErr(mapApiError(r.error));
                                        await refreshFavorites().catch(() => null);
                                      }}
                                    >
                                      <StarRoundedIcon />
                                    </IconButton>
                                  </Stack>
                                </CardContent>
                              </CardActionArea>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ) : null}

          {tab === "profile" ? (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography fontWeight={900}>Профиль</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Можно поменять отображаемое имя/аватар для MLDSL Hub (не влияет на Discord).
                  </Typography>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }} alignItems="stretch">
                    <Card variant="outlined" sx={{ flex: 1 }}>
                      <CardContent>
                        <Stack spacing={1.25}>
                          <TextField label="Имя" value={profileUsername} onChange={(e) => setProfileUsername(e.target.value)} fullWidth />
                          <TextField label="Аватар (URL)" value={profileAvatar} onChange={(e) => setProfileAvatar(e.target.value)} fullWidth />
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                            <TextField
                              label="Цвет ника"
                              type="color"
                              value={profileNameColor}
                              onChange={(e) => setProfileNameColor(e.target.value)}
                              sx={{ width: { xs: "100%", sm: 170 } }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                              label="Цвет топбара"
                              type="color"
                              value={profileTopbarColor}
                              onChange={(e) => setProfileTopbarColor(e.target.value)}
                              sx={{ width: { xs: "100%", sm: 170 } }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <Box sx={{ flex: 1 }} />
                            <Chip
                              size="small"
                              variant="outlined"
                              label="Превью"
                              sx={{
                                bgcolor: profileTopbarColor,
                                color: profileNameColor,
                                borderColor: "rgba(255,255,255,0.18)",
                                fontWeight: 900,
                              }}
                            />
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              variant="contained"
                              onClick={async () => {
                                setErr("");
                                const r = await api.updateProfile({
                                  username: profileUsername,
                                  avatar: profileAvatar,
                                  nameColor: profileNameColor,
                                  topbarColor: profileTopbarColor,
                                });
                                if ("error" in r) return setErr(mapApiError(r.error));
                                await refreshMe();
                                await refreshFeed();
                              }}
                            >
                              Сохранить
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={async () => {
                                setProfileAvatar("");
                                setProfileUsername(me?.username || "");
                                setProfileNameColor("#ffffff");
                                setProfileTopbarColor("#111827");
                              }}
                            >
                              Сбросить
                            </Button>
                          </Stack>

                          <Divider sx={{ my: 1 }} />

                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Button
                              variant="outlined"
                              disabled={!me}
                              onClick={async () => {
                                const r = await api.connectCreate({ client: "mldsl" });
                                if ("error" in r) return setErr(mapApiError(r.error));
                                const hub = window.location.origin;
                                const url = `mldsl://connect?hub=${encodeURIComponent(hub)}&code=${encodeURIComponent(r.code)}`;
                                window.location.href = url;
                                flashErr("Открываю MLDSL для подключения… (если ничего не произошло — проверь, что MLDSL установлен)", 6500);
                              }}
                            >
                              Подключить MLDSL
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ flex: 1 }}>
                      <CardContent>
                        <Typography fontWeight={900}>Статистика</Typography>
                        {profileStats ? (
                          <Stack spacing={1} sx={{ mt: 1.25 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Chip size="small" variant="outlined" label={`постов: ${profileStats.postsCount}`} />
                              <Chip size="small" variant="outlined" icon={<FavoriteRoundedIcon />} label={`лайки всего: ${profileStats.totalScore}`} color={profileStats.totalScore < 0 ? "error" : undefined} />
                              <Chip size="small" variant="outlined" label={`среднее: ${profileStats.avgScore.toFixed(2)}`} />
                              {(() => {
                                const r = repLabel(profileStats.avgScore);
                                return <Chip size="small" variant="outlined" icon={<VerifiedRoundedIcon />} label={`репутация: ${r.label}`} color={r.color} />;
                              })()}
                            </Stack>
                          </Stack>
                        ) : (
                          <Typography sx={{ mt: 1 }} color="text.secondary">
                            Загрузка…
                          </Typography>
                        )}
                      </CardContent>
                    </Card>

                    {me?.isAdmin ? (
                      <Card variant="outlined" sx={{ flex: 1 }}>
                        <CardContent>
                          <Typography fontWeight={900}>Админ: UI</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Глобальные настройки оформления (для всех пользователей).
                          </Typography>
                          <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                              <TextField
                                label="Цвет рейтинга 0"
                                type="color"
                                value={adminZeroColor}
                                onChange={(e) => setAdminZeroColor(e.target.value)}
                                sx={{ width: { xs: "100%", sm: 170 } }}
                                InputLabelProps={{ shrink: true }}
                              />
                              <TextField
                                label="Акцент рейтинга 0"
                                type="color"
                                value={adminZeroAccent}
                                onChange={(e) => setAdminZeroAccent(e.target.value)}
                                sx={{ width: { xs: "100%", sm: 170 } }}
                                InputLabelProps={{ shrink: true }}
                              />
                              <TextField
                                label="Карточка (светлая) от"
                                type="color"
                                value={adminPostCardLightFrom}
                                onChange={(e) => setAdminPostCardLightFrom(e.target.value)}
                                sx={{ width: { xs: "100%", sm: 170 } }}
                                InputLabelProps={{ shrink: true }}
                              />
                              <TextField
                                label="Карточка (светлая) к"
                                type="color"
                                value={adminPostCardLightTo}
                                onChange={(e) => setAdminPostCardLightTo(e.target.value)}
                                sx={{ width: { xs: "100%", sm: 170 } }}
                                InputLabelProps={{ shrink: true }}
                              />
                              <Box sx={{ flex: 1 }} />
                              <Chip
                                size="small"
                                label="Превью 0"
                                icon={<FavoriteRoundedIcon sx={{ color: "inherit !important" }} />}
                                sx={{
                                  background: `linear-gradient(135deg, ${adminZeroColor}, ${adminZeroAccent})`,
                                  color: "rgba(255,255,255,0.92)",
                                  border: "1px solid rgba(255,255,255,0.14)",
                                  fontWeight: 900,
                                }}
                              />
                              <Chip
                                size="small"
                                label="Превью карточки"
                                sx={{
                                  background: `linear-gradient(180deg, ${adminPostCardLightFrom} 0%, ${adminPostCardLightTo} 100%)`,
                                  color: "rgba(17,24,39,0.92)",
                                  border: "1px solid rgba(37,99,235,0.24)",
                                  fontWeight: 900,
                                }}
                              />
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button
                                variant="contained"
                                onClick={async () => {
                                  const r = await api.updateAdminSettings({
                                    scoreZeroColor: adminZeroColor,
                                    scoreZeroAccent: adminZeroAccent,
                                    postCardLightFrom: adminPostCardLightFrom,
                                    postCardLightTo: adminPostCardLightTo,
                                  });
                                  if ("error" in r) return setErr(mapApiError(r.error));
                                  setRampOverrides({ zeroBase: adminZeroColor, zeroAccent: adminZeroAccent });
                                  setPubStatus("сохранено");
                                  setTimeout(() => setPubStatus(""), 900);
                                }}
                              >
                                Сохранить
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  setAdminUsersOpen(true);
                                  runAdminUsersSearch(adminUsersQ).catch((e) => setErr(String(e?.message || e)));
                                }}
                              >
                                Пользователи
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={async () => {
                                  const r = await api.adminAnalytics();
                                  if ("error" in r) return setErr(mapApiError(r.error));
                                  if (!r.enabled) return setErr("Umami не включен: укажи UMAMI_WEBSITE_ID в окружении.");
                                  window.open(r.dashboardUrl, "_blank", "noopener,noreferrer");
                                }}
                              >
                                Аналитика
                              </Button>
                              <Button
                                variant="outlined"
                                startIcon={<RestoreRoundedIcon />}
                                disabled={adminRecalcBusy}
                                onClick={async () => {
                                  const ok = window.confirm(
                                    "Пересчитать требуемый ранг для всех постов и всех изданий? Это может занять время."
                                  );
                                  if (!ok) return;
                                  setAdminRecalcBusy(true);
                                  setAdminRecalcResult("");
                                  try {
                                    const r = await api.adminRecomputeAllPostRanks();
                                    if ("error" in r) {
                                      setErr(mapApiError(r.error));
                                      return;
                                    }
                                    const tail =
                                      Number(r.failedPosts || 0) > 0
                                        ? `, ошибок: ${Number(r.failedPosts || 0)} (подробности в API ответе)`
                                        : "";
                                    setAdminRecalcResult(
                                      `перерасчёт рангов: постов=${Number(r.scannedPosts || 0)}, обновлено постов=${Number(
                                        r.updatedPosts || 0
                                      )}, обновлено изданий=${Number(r.updatedConfigs || 0)}${tail}`
                                    );
                                  } finally {
                                    setAdminRecalcBusy(false);
                                  }
                                }}
                              >
                                {adminRecalcBusy ? "Пересчёт..." : "Перерасчёт рангов"}
                              </Button>
                              <Button
                                variant="outlined"
                                startIcon={<UploadFileRoundedIcon />}
                                disabled={adminBackupBusy}
                                onClick={async () => {
                                  setAdminBackupBusy(true);
                                  setAdminBackupResult("");
                                  try {
                                    const r = await api.adminBackupTestUpload();
                                    if ("error" in r) {
                                      setErr(mapApiError(r.error));
                                      return;
                                    }
                                    const view = String(r.uploaded?.webViewLink || "").trim();
                                    setAdminBackupResult(view ? `тест загружен: ${view}` : `тест загружен: ${r.uploaded?.name || "ok"}`);
                                  } finally {
                                    setAdminBackupBusy(false);
                                  }
                                }}
                              >
                                {adminBackupBusy ? "Загрузка..." : "Тест Drive upload"}
                              </Button>
                              <Button
                                variant="outlined"
                                startIcon={<UploadFileRoundedIcon />}
                                disabled={adminBackupBusy}
                                onClick={async () => {
                                  setAdminBackupBusy(true);
                                  setAdminBackupResult("");
                                  try {
                                    const r = await api.adminBackupRun(true);
                                    if ("error" in r) {
                                      setErr(mapApiError(r.error));
                                      return;
                                    }
                                    if (r.skipped) {
                                      setAdminBackupResult("backup: без изменений, новая версия не загружена");
                                    } else {
                                      const view = String(r.uploaded?.webViewLink || "").trim();
                                      const del = Number(r.retention?.deleted || 0);
                                      const extra = del > 0 ? `, удалено старых: ${del}` : "";
                                      setAdminBackupResult(
                                        view ? `backup загружен: ${view}${extra}` : `backup загружен: ${r.uploaded?.name || "ok"}${extra}`
                                      );
                                    }
                                  } finally {
                                    setAdminBackupBusy(false);
                                  }
                                }}
                              >
                                {adminBackupBusy ? "Загрузка..." : "Backup run (force)"}
                              </Button>
                              <Typography variant="body2" color="text.secondary">
                                {pubStatus}
                              </Typography>
                            </Stack>
                            {adminBackupResult ? (
                              <Typography variant="caption" color="text.secondary">
                                {adminBackupResult}
                              </Typography>
                            ) : null}
                            {adminRecalcResult ? (
                              <Typography variant="caption" color="text.secondary">
                                {adminRecalcResult}
                              </Typography>
                            ) : null}
                          </Stack>
                        </CardContent>
                      </Card>
                    ) : null}
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar src={me?.avatar} imgProps={{ referrerPolicy: "no-referrer" }} sx={{ width: 34, height: 34 }}>
                        {(me?.username || "?").slice(0, 1)}
                      </Avatar>
                      <Typography fontWeight={900}>
                        <span style={{ color: me?.nameColor || "inherit" }}>{me?.username || "Профиль"}</span>
                      </Typography>
                    </Stack>
                    {profileHasMore ? (
                      <Button size="small" variant="outlined" disabled={profilePaging} onClick={() => loadMoreProfilePosts().catch((e) => setErr(String(e?.message || e)))}>
                        {profilePaging ? "Загрузка…" : "Ещё проекты"}
                      </Button>
                    ) : null}
                  </Stack>

                  {!profilePosts.length ? (
                    <Typography sx={{ mt: 1 }} color="text.secondary">
                      Пока нет постов.
                    </Typography>
                  ) : (
                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                      {profilePosts.map((p) => {
                        const r = repLabel(p.score);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={p.id}>
                            <Card variant="outlined" sx={postCardSx()}>
                              <CardActionArea
                                sx={{ height: "100%", alignItems: "stretch" }}
                                onClick={() => openPost(p.id).catch((e) => setErr(String(e?.message || e)))}
                              >
                                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
                                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                    <Typography fontWeight={900} variant="subtitle1" noWrap sx={{ minWidth: 0, flex: 1 }}>
                                      {p.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                      {fmtDate(p.created_at)}
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                    {p.description || ""}
                                  </Typography>
                                  {p.authorUsername
                                    ? authorPill({
                                        username: p.authorUsername,
                                        avatar: p.authorAvatar,
                                        score: typeof p.authorScore === "number" ? p.authorScore : undefined,
                                        nameColor: p.authorNameColor,
                                        onClick: () => {
                                          if (p.authorId) openUser(p.authorId).catch((e) => setErr(String(e?.message || e)));
                                        },
                                      })
                                    : null}
                                  {postStatsRow(p)}
                                  <Box sx={{ flex: 1 }} />
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Chip
                                      size="small"
                                      label={tierRangeLabel((p as any).minRequiredTier || p.requiredTier, (p as any).maxRequiredTier || p.requiredTier)}
                                      sx={tierRangeChipSx((p as any).minRequiredTier || p.requiredTier, (p as any).maxRequiredTier || p.requiredTier)}
                                    />
                                    <Chip size="small" variant="outlined" icon={<VerifiedRoundedIcon />} label={r.label} color={r.color} />
                                    <Chip size="small" icon={<FavoriteRoundedIcon sx={{ color: "inherit !important" }} />} label={String(p.score)} sx={scoreChipSx(p.score)} />
                                    {p.hidden ? <Chip size="small" color="error" variant="outlined" icon={<VisibilityOffRoundedIcon />} label="скрыто" /> : null}
                                    <Box sx={{ flex: 1 }} />
                                  </Stack>
                                </CardContent>
                              </CardActionArea>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ) : null}
        </Grid>
        <Box sx={{ mt: 2, textAlign: "left" }}>
          <Typography variant="body2" color="text.secondary">
            Сделано Universe •{" "}
            <Button
              component="a"
              href="/LICENSE.txt"
              target="_blank"
              rel="noreferrer"
              size="small"
              sx={{ minWidth: 0, px: 0.5, textTransform: "none" }}
            >
              лицензия
            </Button>
          </Typography>
        </Box>
      </Container>

      <Dialog
        open={postOpen}
        onClose={closePostModal}
        fullScreen
        maxWidth="lg"
        fullWidth
      >
        <Toolbar sx={{ gap: 1 }}>
          {inlineEdit ? (
            <TextField
              size="small"
              value={inlineTitle}
              onChange={(e) => setInlineTitle(e.target.value)}
              sx={{ flex: 1 }}
            />
          ) : (
            <Typography
              fontWeight={900}
              sx={{ flex: 1 }}
              noWrap
            >
              {post?.title || "Пост"}
            </Typography>
          )}
          {me && post ? (
            <Tooltip title={isFav ? "Убрать из избранного" : "В избранное"}>
              <IconButton
                color={isFav ? "primary" : "default"}
                onClick={async () => {
                  const next = !isFav;
                  // optimistic
                  setFavoriteIds((prev) => {
                    const n = new Set(prev);
                    if (next) n.add(post.id);
                    else n.delete(post.id);
                    return n;
                  });
                  if (next) {
                    setFavorites((prev) => [post as unknown as PostListItem, ...prev.filter((x) => x.id !== post.id)]);
                  } else {
                    setFavorites((prev) => prev.filter((x) => x.id !== post.id));
                  }

                  const r = await api.setFavorite(post.id, next);
                  if ("error" in r) {
                    await refreshFavorites().catch(() => null);
                    return setErr(mapApiError(r.error));
                  }
                }}
              >
                {isFav ? <StarRoundedIcon /> : <StarBorderRoundedIcon />}
              </IconButton>
            </Tooltip>
          ) : null}
          <Button
            variant="outlined"
            startIcon={<ContentCopyRoundedIcon />}
            disabled={!post}
            onClick={async () => {
              if (!post) return;
              const cfg = String(postActiveConfigId || (post as any)?.activeConfigId || "").trim();
              const cmd = cfg ? `/mldsl run ${post.id} --config ${cfg}` : `/mldsl run ${post.id}`;
              await copyToClipboard(cmd);
              setPubStatus("скопировано");
              setTimeout(() => setPubStatus(""), 900);
            }}
          >
            Распечатать
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadRoundedIcon />}
            disabled={!post}
            onClick={() => {
              void downloadPostZip();
            }}
          >
            Скачать
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInFullRoundedIcon />}
            disabled={!post}
            onClick={() => {
              setCodeActiveIdx(codeFiles.length === 1 ? 0 : -1);
              setCodeOpen(true);
            }}
          >
            Код
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryRoundedIcon />}
            disabled={!post}
            onClick={() => {
              void openPostVersions();
            }}
          >
            Версии
          </Button>
          {me && post && (post.authorId === me.id || me.isAdmin) ? (
            <Button
              variant="outlined"
              onClick={async () => {
                if (!post) return;
                await loadPublishFromPost(post);
              }}
            >
              Редактировать
            </Button>
          ) : null}
          {me && post && (post.authorId === me.id || me.isAdmin) ? (
            <Button
              variant="outlined"
              color="error"
              disabled={deletePostBusy}
              onClick={async () => {
                if (!post || deletePostBusy) return;
                const ok = window.confirm("Удалить пост безвозвратно? История версий и файлы будут удалены.");
                if (!ok) return;
                setDeletePostBusy(true);
                try {
                  const r = await api.deletePost(post.id);
                  if ("error" in r) {
                    setErr(mapApiError(r.error));
                    return;
                  }
                  closePostModal();
                  setPost(null);
                  await refreshFeed();
                  if (tab === "profile") {
                    await loadProfilePosts().catch((e) => setErr(String(e?.message || e)));
                  }
                } finally {
                  setDeletePostBusy(false);
                }
              }}
            >
              {deletePostBusy ? "Удаление..." : "Удалить пост"}
            </Button>
          ) : null}
          <Button variant="contained" onClick={closePostModal}>
            Закрыть
          </Button>
        </Toolbar>
        <Divider />
        <DialogContent sx={{ bgcolor: "background.default" }}>
          {!post ? (
            <Typography color="text.secondary">Загрузка…</Typography>
          ) : (
              <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {post.authorUsername
                  ? authorPill({
                      username: post.authorUsername,
                      avatar: post.authorAvatar,
                      score: typeof post.authorScore === "number" ? post.authorScore : undefined,
                      nameColor: post.authorNameColor,
                      onClick: () => {
                        if (post.authorId) openUser(post.authorId).catch((e) => setErr(String(e?.message || e)));
                      },
                    })
                  : null}
                <Chip size="small" label={tierPrintLabel(post.requiredTier)} sx={tierChipSx(post.requiredTier)} />
                {((post as any)?.minRequiredTier || post.requiredTier) !== ((post as any)?.maxRequiredTier || post.requiredTier) ? (
                  <Chip
                    size="small"
                    label={tierRangeLabel((post as any)?.minRequiredTier || post.requiredTier, (post as any)?.maxRequiredTier || post.requiredTier)}
                    sx={tierRangeChipSx((post as any)?.minRequiredTier || post.requiredTier, (post as any)?.maxRequiredTier || post.requiredTier)}
                  />
                ) : null}
                {rep ? <Chip size="small" variant="outlined" icon={<VerifiedRoundedIcon />} label={rep.label} color={rep.color} /> : null}
                {post.version ? <Chip size="small" variant="outlined" label={`v${post.version}`} /> : null}
                <Chip size="small" icon={<FavoriteRoundedIcon sx={{ color: "inherit !important" }} />} label={String(post.score)} sx={scoreChipSx(post.score)} />
                {me?.isAdmin ? <Chip size="small" variant="outlined" label={`репорты: ${post.reportsCount}`} /> : null}
                {post.hidden ? <Chip size="small" color="error" variant="outlined" icon={<VisibilityOffRoundedIcon />} label="скрыто" /> : null}
              </Stack>

              {Array.isArray((post as any)?.configs) && (post as any).configs.length > 1 ? (
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.25 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight={800}>
                        Издания:
                      </Typography>
                      {((post as any).configs || []).map((cfg: any) => {
                        const active = String(cfg?.id || "") === String(postActiveConfigId || (post as any)?.activeConfigId || "");
                        return (
                          <Chip
                            key={String(cfg?.id || "")}
                            size="small"
                            clickable
                            color={active ? "primary" : "default"}
                            variant={active ? "filled" : "outlined"}
                            label={String(cfg?.title || cfg?.id || "config")}
                            onClick={() => {
                              if (!post) return;
                              openPost(post.id, me ? { id: me.id, isAdmin: !!me.isAdmin } : null, String(cfg?.id || "")).catch((e) =>
                                setErr(String(e?.message || e))
                              );
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}

              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography fontWeight={900}>Файлы</Typography>
                    {inlineEdit ? (
                      <Button component="label" size="small" variant="outlined" startIcon={<UploadFileRoundedIcon />}>
                        Залить файл
                        <input
                          type="file"
                          hidden
                          multiple
                          accept=".mldsl,.json,text/plain,application/json"
                          onChange={async (e) => {
                            await appendInlineFiles(Array.from(e.target.files || []));
                          }}
                        />
                      </Button>
                    ) : null}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Чтобы посмотреть код — кликни по файлу.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                    {(inlineEdit ? inlineFiles : codeFiles).map((f, idx) => (
                      <Chip
                        key={`${f.name}-${idx}`}
                        icon={<InsertDriveFileRoundedIcon />}
                        label={`${f.name} (${f.size} B)`}
                        onDelete={
                          inlineEdit
                            ? () => {
                                const next = inlineFiles.filter((_, j) => j !== idx);
                                setInlineFiles(next);
                                setInlineCode(buildBundleFromFiles(next));
                              }
                            : undefined
                        }
                        deleteIcon={inlineEdit ? <DeleteRoundedIcon /> : undefined}
                        onClick={() => {
                          if (inlineEdit) return;
                          setCodeActiveIdx(idx);
                          setCodeOpen(true);
                        }}
                        variant={idx === codeActiveIdx ? "filled" : "outlined"}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              {inlineEdit ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label="Описание"
                  value={inlineDesc}
                  onChange={(e) => setInlineDesc(e.target.value)}
                />
              ) : (
                <Box
                  sx={{
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1.5,
                  }}
                  dangerouslySetInnerHTML={{ __html: safePostDescHtml || "<em>Пусто</em>" }}
                />
              )}

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<ThumbUpRoundedIcon />}
                  disabled={!post || isVoteLocked(post.id)}
                  onClick={async () => {
                    if (!post || isVoteLocked(post.id)) return;
                    await runWithVoteLock(post.id, async () => {
                      // optimistic
                      setPost({ ...post, score: post.score + 1 });
                      setPosts((prev) => prev.map((x) => (x.id === post.id ? { ...x, score: x.score + 1 } : x)));
                      const r = await api.vote(post.id, +1);
                      if ("error" in r) {
                        setPost({ ...post, score: post.score });
                        setPosts((prev) => prev.map((x) => (x.id === post.id ? { ...x, score: post.score } : x)));
                        return setErr(mapApiError(r.error));
                      }
                      await openPost(post.id, me ? { id: me.id, isAdmin: !!me.isAdmin } : null, postActiveConfigId || (post as any)?.activeConfigId || undefined);
                      await refreshFeed();
                    });
                  }}
                >
                  Лайк
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ThumbDownRoundedIcon />}
                  disabled={!post || isVoteLocked(post.id)}
                  onClick={async () => {
                    if (!post || isVoteLocked(post.id)) return;
                    await runWithVoteLock(post.id, async () => {
                      setPost({ ...post, score: post.score - 1 });
                      setPosts((prev) => prev.map((x) => (x.id === post.id ? { ...x, score: x.score - 1 } : x)));
                      const r = await api.vote(post.id, -1);
                      if ("error" in r) {
                        setPost({ ...post, score: post.score });
                        setPosts((prev) => prev.map((x) => (x.id === post.id ? { ...x, score: post.score } : x)));
                        return setErr(mapApiError(r.error));
                      }
                      await openPost(post.id, me ? { id: me.id, isAdmin: !!me.isAdmin } : null, postActiveConfigId || (post as any)?.activeConfigId || undefined);
                      await refreshFeed();
                    });
                  }}
                >
                  Дизлайк
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ReportRoundedIcon />}
                  onClick={async () => {
                    const reason = prompt("Причина репорта (гриф/обман/спам):", "гриф");
                    if (!reason) return;
                    const r = await api.report(post.id, reason);
                    if ("error" in r) return setErr(mapApiError(r.error));
                    await openPost(post.id, me ? { id: me.id, isAdmin: !!me.isAdmin } : null, postActiveConfigId || (post as any)?.activeConfigId || undefined);
                    await refreshFeed();
                  }}
                >
                  Репорт
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography fontWeight={800}>Риск‑флаги:</Typography>
                {post.riskFlags?.length ? post.riskFlags.map((f) => <Chip key={f} size="small" color="error" label={f} />) : <Typography color="text.secondary">нет</Typography>}
              </Stack>

              <Divider />

              <Typography fontWeight={900}>Код (превью)</Typography>
              <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 2, border: "1px solid", borderColor: "divider" }}>
                <Typography component="pre" sx={{ m: 0, whiteSpace: "pre-wrap", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12 }}>
                  {post.code}
                </Typography>
              </Box>

              <Divider />

              <Typography fontWeight={900}>Комментарии</Typography>
              <Stack spacing={1}>
                {post.comments?.length ? (
                  post.comments.map((c) => (
                    <Card key={c.id} variant="outlined" sx={{ ml: c.parentId ? 3 : 0 }}>
                      <CardContent sx={{ py: 1.25 }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <Avatar src={c.avatar} imgProps={{ referrerPolicy: "no-referrer" }} sx={{ width: 24, height: 24 }}>
                              {c.username.slice(0, 1)}
                            </Avatar>
                            <Typography fontWeight={800} noWrap sx={{ maxWidth: 220 }}>
                              {c.username}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {fmtDate(c.createdAt)}
                          </Typography>
                        </Stack>
                        {c.parentId ? (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                            Ответ на комментарий
                          </Typography>
                        ) : null}
                        {editingCommentId === c.id ? (
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                            <TextField
                              size="small"
                              fullWidth
                              value={editingCommentBody}
                              onChange={(e) => setEditingCommentBody(e.target.value)}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={async () => {
                                const v = editingCommentBody.trim();
                                if (!v) return;
                                const r = await api.editComment(post.id, c.id, v);
                                if ("error" in r) return setErr(mapApiError(r.error));
                                setEditingCommentId(null);
                                setEditingCommentBody("");
                                await openPost(post.id, me ? { id: me.id, isAdmin: !!me.isAdmin } : null, postActiveConfigId || (post as any)?.activeConfigId || undefined);
                              }}
                            >
                              Сохранить
                            </Button>
                            <IconButton size="small" onClick={() => { setEditingCommentId(null); setEditingCommentBody(""); }}>
                              <CloseRoundedIcon />
                            </IconButton>
                          </Stack>
                        ) : (
                          <Typography sx={{ mt: 0.75, whiteSpace: "pre-wrap" }}>{c.body}</Typography>
                        )}
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                          <Button
                            size="small"
                            startIcon={<ReplyRoundedIcon />}
                            onClick={() => {
                              setReplyToCommentId(c.id);
                              const el = document.getElementById("commentBody") as HTMLInputElement | null;
                              if (el) el.focus();
                            }}
                          >
                            Ответить
                          </Button>
                          {c.canEdit ? (
                            <Button
                              size="small"
                              startIcon={<EditRoundedIcon />}
                              onClick={() => {
                                setEditingCommentId(c.id);
                                setEditingCommentBody(c.body || "");
                              }}
                            >
                              Редактировать
                            </Button>
                          ) : null}
                          {c.updatedAt ? (
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center", ml: 1 }}>
                              изменено: {fmtDate(c.updatedAt)}
                            </Typography>
                          ) : null}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Typography color="text.secondary">пока нет</Typography>
                )}

                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={replyToCommentId ? "Ответ на комментарий" : "Комментарий"}
                    id="commentBody"
                    onKeyDown={async (e) => {
                      if (e.key !== "Enter" || e.shiftKey) return;
                      e.preventDefault();
                      const v = (e.currentTarget as HTMLInputElement).value.trim();
                      if (!v) return;
                      const r = await api.comment(post.id, v, replyToCommentId || undefined);
                      if ("error" in r) return setErr(mapApiError(r.error));
                      (e.currentTarget as HTMLInputElement).value = "";
                      setReplyToCommentId(null);
                      await openPost(post.id, me ? { id: me.id, isAdmin: !!me.isAdmin } : null, postActiveConfigId || (post as any)?.activeConfigId || undefined);
                      await refreshFeed();
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={async () => {
                      const el = document.getElementById("commentBody") as HTMLInputElement | null;
                      const v = (el?.value || "").trim();
                      if (!v) return;
                      const r = await api.comment(post.id, v, replyToCommentId || undefined);
                      if ("error" in r) return setErr(mapApiError(r.error));
                      if (el) el.value = "";
                      setReplyToCommentId(null);
                      await openPost(post.id, me ? { id: me.id, isAdmin: !!me.isAdmin } : null, postActiveConfigId || (post as any)?.activeConfigId || undefined);
                      await refreshFeed();
                    }}
                  >
                    <SendRoundedIcon />
                  </IconButton>
                  {replyToCommentId ? (
                    <IconButton
                      color="default"
                      onClick={() => setReplyToCommentId(null)}
                    >
                      <CloseRoundedIcon />
                    </IconButton>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={postVersionsOpen} onClose={() => setPostVersionsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>История версий поста</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={postVersionsShowAll} onChange={(e) => setPostVersionsShowAll(e.target.checked)} />}
              label="Показать все издания"
            />
          </Stack>
          {postVersionsLoading ? (
            <Typography color="text.secondary">Загрузка…</Typography>
          ) : (
            <Stack spacing={1.5}>
              {!postVersions.length ? <Typography color="text.secondary">История пуста.</Typography> : null}
              {postVersions.map((v) => (
                <Card key={`${v.id}-${v.version}`} variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" variant="outlined" label={`v${v.version}`} />
                        {(v as any).configKey ? <Chip size="small" variant="outlined" label={`издание: ${String((v as any).configKey)}`} /> : null}
                        <Typography fontWeight={800}>{v.title || "Без названия"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {fmtDate(v.createdAt)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedVersionCode(buildQuickDiff(v.code || "", post?.code || ""));
                          }}
                        >
                          Diff
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          startIcon={<RestoreRoundedIcon />}
                          disabled={!post || rollbackBusy === v.version}
                          onClick={async () => {
                            if (!post) return;
                            const rollbackCfg =
                              String((v as any)?.configKey || "") ||
                              String(postActiveConfigId || (post as any)?.activeConfigId || "");
                            const rollbackScope = rollbackCfg ? `издания '${rollbackCfg}'` : "всего поста";
                            if (!confirm(`Откатить версию v${v.version} для ${rollbackScope}?`)) return;
                            setRollbackBusy(v.version);
                            try {
                              const r = await api.rollbackPostVersion(post.id, v.version, rollbackCfg || undefined);
                              if ("error" in r) {
                                setErr(mapApiError(r.error));
                                return;
                              }
                              await openPost(post.id, me ? { id: me.id, isAdmin: !!me.isAdmin } : null, rollbackCfg || undefined);
                              await refreshFeed();
                              setPubStatus(`Откат к v${v.version} выполнен`);
                              window.setTimeout(() => setPubStatus(""), 1400);
                            } finally {
                              setRollbackBusy(null);
                            }
                          }}
                        >
                          {rollbackBusy === v.version ? "Откат..." : "Rollback"}
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {selectedVersionCode ? (
                <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 2, border: "1px solid", borderColor: "divider" }}>
                  <Typography fontWeight={800} sx={{ mb: 1 }}>
                    Diff (быстрый)
                  </Typography>
                  <Typography
                    component="pre"
                    sx={{ m: 0, whiteSpace: "pre-wrap", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12 }}
                  >
                    {selectedVersionCode}
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostVersionsOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={codeOpen}
        onClose={() => setCodeOpen(false)}
        fullScreen
        maxWidth="lg"
        fullWidth
      >
        <Toolbar sx={{ gap: 1 }}>
          <Typography fontWeight={900} sx={{ flex: 1 }}>
            Код
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ContentCopyRoundedIcon />}
            onClick={async () => {
              const text = codeActiveIdx >= 0 ? codeFiles[codeActiveIdx]?.text : post?.code || "";
              await copyToClipboard(text);
              setPubStatus("скопировано");
              setTimeout(() => setPubStatus(""), 900);
            }}
          >
            Копировать
          </Button>
          <Button variant="contained" onClick={() => setCodeOpen(false)}>
            Закрыть
          </Button>
        </Toolbar>
        <DialogContent sx={{ bgcolor: "background.default" }}>
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              Выбери файл:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {codeFiles.map((f, idx) => (
                <Chip
                  key={`${f.name}-${idx}`}
                  icon={<InsertDriveFileRoundedIcon />}
                  label={f.name}
                  variant={idx === codeActiveIdx ? "filled" : "outlined"}
                  onClick={() => setCodeActiveIdx(idx)}
                />
              ))}
            </Stack>

            {codeActiveIdx < 0 ? (
              <Typography color="text.secondary">Кликни по файлу, чтобы открыть код.</Typography>
            ) : (
              <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 2, border: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" fontWeight={900}>
                    {codeFiles[codeActiveIdx]?.name || "Файл"}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadRoundedIcon />}
                    onClick={() => {
                      const f = codeFiles[codeActiveIdx];
                      if (!f) return;
                      downloadTextFile(f.name || "file.mldsl", f.text || "");
                    }}
                  >
                    Скачать файл
                  </Button>
                </Stack>
                <Typography component="pre" sx={{ m: 0, whiteSpace: "pre-wrap", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12 }}>
                  {codeFiles[codeActiveIdx]?.text || ""}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={userOpen}
        onClose={() => {
          setUserOpen(false);
        }}
        fullScreen
        maxWidth="lg"
        fullWidth
      >
        <Toolbar sx={{ gap: 1 }}>
          <Typography fontWeight={900} sx={{ flex: 1 }} noWrap>
            {userInfo ? userInfo.username : "Профиль"}
          </Typography>
          <Button variant="contained" onClick={() => setUserOpen(false)}>
            Закрыть
          </Button>
        </Toolbar>
        <Divider />
        <DialogContent sx={{ bgcolor: "background.default" }}>
          {!userInfo ? (
            <Typography color="text.secondary">Загрузка…</Typography>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Avatar src={userInfo.avatar} imgProps={{ referrerPolicy: "no-referrer" }} sx={{ width: 46, height: 46 }}>
                  {userInfo.username.slice(0, 1)}
                </Avatar>
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography fontWeight={900} noWrap sx={{ maxWidth: 380 }}>
                    <span style={{ color: userInfo.nameColor || "inherit" }}>{userInfo.username}</span>
                  </Typography>
                  {userStats ? (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip size="small" variant="outlined" label={`постов: ${userStats.postsCount}`} />
                      <Chip
                        size="small"
                        icon={<FavoriteRoundedIcon sx={{ color: "inherit !important" }} />}
                        label={`лайки всего: ${userStats.totalScore}`}
                        sx={scoreChipSx(userStats.totalScore)}
                      />
                      <Chip size="small" variant="outlined" label={`среднее: ${userStats.avgScore.toFixed(2)}`} />
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Загрузка статистики…
                    </Typography>
                  )}
                </Stack>
              </Stack>

              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography fontWeight={900}>Проекты</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        clickable
                        color={userSort === "latest" ? "primary" : "default"}
                        label="Новые"
                        onClick={async () => {
                          if (!userId) return;
                          setUserSort("latest");
                          setUserPaging(false);
                          const r = await api.userPostsPaged(userId, { sort: "latest", limit: 30 });
                          if ("error" in r) return setErr(mapApiError(r.error));
                          setUserPosts(r.items || []);
                          setUserCursor(r.nextCursor || null);
                          setUserHasMore(!!r.nextCursor);
                        }}
                      />
                      <Chip
                        clickable
                        color={userSort === "top" ? "primary" : "default"}
                        label="Топ"
                        onClick={async () => {
                          if (!userId) return;
                          setUserSort("top");
                          setUserPaging(false);
                          const r = await api.userPostsPaged(userId, { sort: "top", limit: 30 });
                          if ("error" in r) return setErr(mapApiError(r.error));
                          setUserPosts(r.items || []);
                          setUserCursor(r.nextCursor || null);
                          setUserHasMore(!!r.nextCursor);
                        }}
                      />
                      {userHasMore ? (
                        <Button size="small" variant="outlined" disabled={userPaging} onClick={() => loadMoreUser().catch((e) => setErr(String(e?.message || e)))}>
                          {userPaging ? "Загрузка…" : "Ещё"}
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>

                  {!userPosts.length ? (
                    <Typography sx={{ mt: 1 }} color="text.secondary">
                      Пока нет постов.
                    </Typography>
                  ) : (
                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                      {userPosts.map((p) => {
                        const r = repLabel(p.score);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={p.id}>
                            <Card variant="outlined" sx={postCardSx()}>
                              <CardActionArea sx={{ height: "100%", alignItems: "stretch" }} onClick={() => openPost(p.id).catch((e) => setErr(String(e?.message || e)))}>
                                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
                                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                    <Typography fontWeight={900} variant="subtitle1" noWrap sx={{ minWidth: 0, flex: 1 }}>
                                      {p.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                      {fmtDate(p.created_at)}
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                    {p.description || ""}
                                  </Typography>
                                  {p.authorUsername
                                    ? authorPill({
                                        username: p.authorUsername,
                                        avatar: p.authorAvatar,
                                        score: typeof p.authorScore === "number" ? p.authorScore : undefined,
                                        nameColor: p.authorNameColor,
                                        onClick: () => {
                                          if (p.authorId) openUser(p.authorId).catch((e) => setErr(String(e?.message || e)));
                                        },
                                      })
                                    : null}
                                  {postStatsRow(p)}
                                  <Box sx={{ flex: 1 }} />
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Chip
                                      size="small"
                                      label={tierRangeLabel((p as any).minRequiredTier || p.requiredTier, (p as any).maxRequiredTier || p.requiredTier)}
                                      sx={tierRangeChipSx((p as any).minRequiredTier || p.requiredTier, (p as any).maxRequiredTier || p.requiredTier)}
                                    />
                                    <Chip size="small" variant="outlined" icon={<VerifiedRoundedIcon />} label={r.label} color={r.color} />
                                    <Chip size="small" icon={<FavoriteRoundedIcon sx={{ color: "inherit !important" }} />} label={String(p.score)} sx={scoreChipSx(p.score)} />
                                    {p.hidden ? <Chip size="small" color="error" variant="outlined" icon={<VisibilityOffRoundedIcon />} label="скрыто" /> : null}
                                    <Box sx={{ flex: 1 }} />
                                  </Stack>
                                </CardContent>
                              </CardActionArea>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload-token dialog removed */}
    </>
  );
}
